// Punto de venta (POS). Operado con teclado: F1 buscar producto · F5 cliente ·
// F6 quitar línea · F9 cancelar · F10 suspender/cambiar · Espacio cobrar.
// El campo de código escanea/teclea barras1/2/3 o código y agrega directo.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uuid } from '@/lib/uuid';
import { Banknote, ImageIcon, Loader2, Maximize2, Minimize2, QrCode, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { abrirCajon, configVigente, debeAbrirCajon, imprimirTicket, imprimirVoucherRecarga, resolverBloquesParaImpresion, type EmpresaTicket, type TicketImprimible } from './print';
import { asegurarBloque, bloqueDisponible, buscarEnCatalogo, catalogoOffline, claveOffline, clientesOffline, conexion, descartarVentaOffline, descontarStockLocal, encolarVenta, formasOffline, porCodigoEnCatalogo, sincronizarVentas, snapshotCatalogo, snapshotClientes, snapshotFormas, ventasPendientes, type VentaOffline } from './offline';
import {
  Caja, ClienteOpt, FormaPago, ProductoBusqueda, ProductoVenta, buscarProductoPorCodigo, buscarProductosPos,
  cajasActivas, cajasOperables, clientesList, clienteCrear, bodegaInfo, descuentoVenta, fidelidadNivel, fidelidadSaldo, fieldCls, formasPagoLocal, getDeviceId, money, redondearMoneda, monederoSaldo, navRowCls, posRpc, PromoEval, promosEvaluar, resolverPrecio, useImpresionConfig,
  SelloPremio, SelloSaldo, sellosPremiosDisponibles, sellosSaldo, productoThumbs, productoImagen, stockProducto, stockProductos, stockProductosOffline, posTicketImprimir,
  StockSucursal, KardexMov, stockPorSucursal, kardexProducto, HistorialCompra, historialCliente, buscarProductosPorMarca, buscarProductosPorPrincipio, buscarProductosPorCategoria, categoriasVenta, CategoriaVenta,
  type PasarelaModos, pasarelaModos,
  BnDesglose, BnProductoFull, BnVentaResp, bnGestionDesglosar, bnGestionProductoAsegurar, bnPendientesCaja, bnProductosActivos, bnRegularizar, bnTecleadoParaRecarga, bnVender,
  BnConfigFull, bnConfigGet, bnRecargaProductoAsegurar, BnWsLog, bnWsLog,
} from './shared';
import { ServiciosBasicos } from './Recargas';
import { MarcaLogo, SelectorCatalogo, marcaDe } from './CatalogoBroadnet';
import { displayCliente, usePuenteHardware } from './hardware';
import { BALANZA_ETIQUETA_DEFAULT, BalanzaEtiquetaCfg, balanzaEtiquetaGet, candidatosPlu, parsearPesoVariable } from './shared';
import { hotelRpc, type HuespedEnCasa } from '@/features/hotel/shared';
import { LOCALE } from '@/lib/utils/regional';

// Visibilidad real del nodo: funciona también con position:fixed (offsetParent
// es null para elementos fixed). getClientRects() = 0 cuando un ancestro tiene
// display:none (pestaña oculta por el keep-alive del TabSystem).
const esVisible = (el: HTMLElement | null) => !!el && el.getClientRects().length > 0;

// --- Modo desktop / pantalla completa (kiosko) ----------------------------
const fsActivo = () => typeof document !== 'undefined' && !!document.fullscreenElement;
async function entrarPantallaCompleta() { try { await document.documentElement.requestFullscreen?.(); } catch { /* gesto requerido / no soportado */ } }
async function salirPantallaCompleta() { try { if (document.fullscreenElement) await document.exitFullscreen?.(); } catch { /* */ } }

// Hook: estado de modo desktop (persistente) + pantalla completa del navegador.
function useModoDesktop() {
  const [desktop, setDesktop] = useState(() => localStorage.getItem('pos_modo_desktop') !== '0'); // por defecto ON
  const [fullscreen, setFullscreen] = useState(fsActivo());
  useEffect(() => {
    const h = () => setFullscreen(fsActivo());
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  // Al entrar en modo desktop, intenta pantalla completa en el primer gesto del
  // usuario (el navegador no permite Fullscreen sin interacción).
  useEffect(() => {
    if (!desktop || fsActivo()) return;
    const once = () => { void entrarPantallaCompleta(); window.removeEventListener('pointerdown', once); window.removeEventListener('keydown', once); };
    window.addEventListener('pointerdown', once);
    window.addEventListener('keydown', once);
    return () => { window.removeEventListener('pointerdown', once); window.removeEventListener('keydown', once); };
  }, [desktop]);
  const activar = useCallback(() => { localStorage.setItem('pos_modo_desktop', '1'); setDesktop(true); void entrarPantallaCompleta(); }, []);
  const salir = useCallback(() => { localStorage.setItem('pos_modo_desktop', '0'); setDesktop(false); void salirPantallaCompleta(); }, []);
  const toggleFs = useCallback(() => { if (fsActivo()) void salirPantallaCompleta(); else void entrarPantallaCompleta(); }, []);
  return { desktop, fullscreen, activar, salir, toggleFs };
}

interface Linea { key: number; producto_id: string; codigo: string; descripcion: string; presentacion?: string | null; marca: string | null; cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number; fracciones?: number; controla_stock?: boolean; precio_manual?: number | null; solo_efectivo?: boolean }
export interface Cliente { id: string; razon_social: string; identificacion: string | null }
interface Pago {
  key: number; forma_pago_id: string; monto: number; referencia: string;
  // Datos de tarjeta (solo si la forma de pago es tipo 'tarjeta') — se cruzan luego
  // con los archivos de Datafast/Medianet para conciliar con el banco.
  adquirente?: string; tarjeta_lote?: string; tarjeta_voucher?: string; tarjeta_autorizacion?: string;
  tarjeta_ultimos4?: string; tarjeta_marca?: string; tarjeta_terminal?: string; tarjeta_diferido_meses?: string;
  /** Lo llenó la pasarela: no se deja reteclear encima (el cajero no tiene el
   *  voucher del datáfono delante y sobrescribirlo rompe la conciliación). */
  pasarela_ok?: boolean;
}
let _k = 1;

// Publica el contexto de la caja para las pantallas del signage (cross-sell +
// pantalla de cliente en caja). Fire-and-forget: jamás frena la venta.
function pushCaja(locationId: string | null, cajaId: string, lineas: { producto_id: string; cantidad: number }[], datos: Record<string, unknown> | null) {
  if (!locationId) return;
  apiClient.post('/rpc/sig_contexto_push', { _location: locationId, _caja: cajaId, _lineas: lineas, _caja_datos: datos }, { headers: { 'Content-Profile': 'signage' } }).catch(() => {});
}

import { CobroPasarela } from './CobroPasarela';

export function PuntoVenta() {
  const deviceId = useMemo(() => getDeviceId(), []);
  const cajaQ = useQuery({ queryKey: ['pos-device-caja', deviceId], queryFn: () => posRpc<Caja | null>('pos_caja_por_device', { p_device_id: deviceId }) });
  const caja = cajaQ.data ?? null;
  const [recambiar, setRecambiar] = useState(false);        // Alt. 2: cambiar caja desde la apertura
  const [aplicandoUrl, setAplicandoUrl] = useState(false);  // Alt. 3: fijar caja por ?caja=CODIGO (kiosco)

  // Alt. 3 — Fija la caja de este equipo desde la URL del acceso directo: ?caja=CODIGO.
  // Cada máquina abre el POS con su propia URL y queda atada a esa caja, sin depender del localStorage.
  useEffect(() => {
    const cod = new URLSearchParams(window.location.search).get('caja');
    if (!cod || cajaQ.isLoading) return;
    if (caja && caja.codigo === cod) return;                // ya opera esa caja: nada que hacer
    let cancel = false;
    (async () => {
      setAplicandoUrl(true);
      try {
        if (caja) {   // no rebindear en silencio si la caja actual tiene una sesión abierta
          const ses = await posRpc<{ id: string } | null>('pos_sesion_activa', { p_caja_id: caja.id });
          if (ses) { toast.error(`Cierra la sesión de ${caja.nombre} antes de cambiar la caja por URL`); return; }
        }
        const cajas = await cajasActivas();
        const destino = cajas.find((c) => c.codigo === cod);
        if (!destino) { toast.error(`No hay una caja activa con código «${cod}»`); return; }
        await posRpc('pos_vincular_device', { p_caja_id: destino.id, p_device_id: deviceId });
        if (!cancel) { toast.success(`Equipo fijado a la caja ${destino.nombre}`); await cajaQ.refetch(); }
      } catch (e) { if (!cancel) toast.error(getErrorMessage(e)); }
      finally { if (!cancel) setAplicandoUrl(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caja, cajaQ.isLoading, deviceId]);

  if (cajaQ.isLoading || aplicandoUrl) return <Centro><Loader2 className="w-6 h-6 animate-spin" /></Centro>;
  if (!caja || recambiar) return <RegistrarTerminal deviceId={deviceId} actual={caja} onDone={() => { setRecambiar(false); void cajaQ.refetch(); }} onCancel={caja ? () => setRecambiar(false) : undefined} />;
  return <ConSesion caja={caja} onCambiarCaja={() => setRecambiar(true)} />;
}

function Centro({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center p-16 text-muted-foreground">{children}</div>;
}

// ---------------------------------------------------------------------------
// Registro del equipo → caja (huella en localStorage)
// ---------------------------------------------------------------------------
function RegistrarTerminal({ deviceId, onDone, actual, onCancel }: { deviceId: string; onDone: () => void; actual?: Caja | null; onCancel?: () => void }) {
  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-operables'], queryFn: cajasOperables });
  const [cajaId, setCajaId] = useState(actual?.id ?? '');
  const [saving, setSaving] = useState(false);
  const registrar = async () => {
    if (!cajaId) return;
    setSaving(true);
    try { await posRpc('pos_vincular_device', { p_caja_id: cajaId, p_device_id: deviceId }); toast.success(actual ? 'Caja del equipo actualizada' : 'Equipo registrado'); onDone(); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <div className="p-6 max-w-md mx-auto space-y-4">
      <h1 className="text-xl font-semibold">{actual ? 'Cambiar caja de este equipo' : 'Registrar este equipo'}</h1>
      <p className="text-sm text-muted-foreground">
        {actual
          ? <>Este equipo opera hoy como <b>{actual.nombre}</b>. Elige la caja a la que quieres cambiarlo (se recordará en este navegador).</>
          : 'Esta máquina aún no está asociada a una caja. Selecciona la caja que operará en este equipo (se recordará en este navegador).'}
      </p>
      <select className={fieldCls} value={cajaId} onChange={(e) => setCajaId(e.target.value)}>
        <option value="">— Selecciona la caja —</option>
        {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
      </select>
      <div className="flex gap-2">
        <Button isLoading={saving} disabled={!cajaId} onClick={registrar}>{actual ? 'Cambiar caja' : 'Registrar equipo como esta caja'}</Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Cancelar</Button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sesión / turno
// ---------------------------------------------------------------------------
function ConSesion({ caja, onCambiarCaja }: { caja: Caja; onCambiarCaja: () => void }) {
  const sesionQ = useQuery({ queryKey: ['pos-sesion', caja.id], queryFn: () => posRpc<{ id: string; codigo: string } | null>('pos_sesion_activa', { p_caja_id: caja.id }) });
  const [fondo, setFondo] = useState('0');
  const [abriendo, setAbriendo] = useState(false);

  if (sesionQ.isLoading) return <Centro><Loader2 className="w-6 h-6 animate-spin" /></Centro>;

  if (!sesionQ.data) {
    const abrir = async () => {
      setAbriendo(true);
      try { await posRpc('pos_abrir_sesion', { p_caja_id: caja.id, p_fondo: Number(fondo) || 0 }); toast.success('Sesión abierta'); sesionQ.refetch(); }
      catch (e) { toast.error(getErrorMessage(e)); } finally { setAbriendo(false); }
    };
    return (
      <div className="p-6 max-w-md mx-auto space-y-4">
        <h1 className="text-xl font-semibold">Abrir caja · {caja.nombre}</h1>
        <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">Fondo inicial</span>
          <input type="number" step="0.01" className={fieldCls} value={fondo} onChange={(e) => setFondo(e.target.value)} /></label>
        <Button isLoading={abriendo} onClick={abrir}>Abrir sesión</Button>
        <button type="button" onClick={onCambiarCaja}
          className="block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">
          ¿Esta no es la caja de este equipo? Cambiar caja
        </button>
      </div>
    );
  }
  return <Venta caja={caja} sesion={sesionQ.data} />;
}

// ---------------------------------------------------------------------------
// Pantalla de venta
// ---------------------------------------------------------------------------
function Venta({ caja, sesion }: { caja: Caja; sesion: { id: string; codigo: string } }) {
  useImpresionConfig();  // deja lista la config del ticket (CFG) antes de imprimir
  const dlg = useDialogoEnLinea();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  // Tipo de documento por transacción: factura (fiscal) | nota de venta (interna, sin IVA/SRI)
  const [tipoDoc, setTipoDoc] = useState<'factura' | 'nota_venta'>('factura');
  // Si la caja no permite Notas de venta, fuerza siempre 'factura' (p.ej. tras recuperar un borrador)
  useEffect(() => { if (caja.permite_nota_venta === false && tipoDoc === 'nota_venta') setTipoDoc('factura'); }, [caja.permite_nota_venta, tipoDoc]);
  // ---- Modo OFFLINE (script 303): estado de conexión + cola de ventas por sincronizar
  const [online, setOnline] = useState(conexion.online);
  const [pendOffline, setPendOffline] = useState(0);
  const [bloqueRestante, setBloqueRestante] = useState(0);
  const refrescarPend = useCallback(() => {
    void ventasPendientes().then((v) => setPendOffline(v.length));
    void bloqueDisponible(caja.id).then(setBloqueRestante);
  }, [caja.id]);
  useEffect(() => { refrescarPend(); }, [refrescarPend]);
  useEffect(() => conexion.suscribir((on) => {
    setOnline(on);
    if (on) {
      toast.info('Conexión restablecida — sincronizando ventas offline…');
      void sincronizarVentas().then((r) => {
        refrescarPend();
        if (r.ok) { toast.success(`${r.ok} venta(s) offline sincronizada(s)`); setVentaSeq((n) => n + 1); }
        if (r.fallidas) toast.error(`${r.fallidas} venta(s) con error — revísalas en el indicador OFFLINE`);
      });
    } else {
      toast.warning('SIN CONEXIÓN — el POS sigue vendiendo en modo offline (solo efectivo)', { duration: 8000 });
    }
  }), [refrescarPend]);
  // Snapshot para operar sin red: catálogo + formas + clientes + bloque de claves
  useEffect(() => {
    if (!online || !caja.location_id) return;
    void snapshotCatalogo(caja.location_id, null).catch(() => {});
    void formasPagoLocal(caja.location_id).then((f) => snapshotFormas(caja.location_id, f)).catch(() => {});
    void clientesList().then(snapshotClientes).catch(() => {});
    void asegurarBloque(caja.id, getDeviceId()).then(() => bloqueDisponible(caja.id).then(setBloqueRestante)).catch(() => {});
    // al montar con red: drena lo que hubiera quedado en cola
    void sincronizarVentas().then((r) => { if (r.ok || r.fallidas) refrescarPend(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, caja.id, caja.location_id]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [sel, setSel] = useState(0);
  const [, setQtyBuf] = useState('');  // buffer de dígitos para la cantidad de la línea seleccionada (se lee vía updater funcional)
  const [codigoFocus, setCodigoFocus] = useState(true);  // foco en el campo de código → escaneo; sin foco → edición de líneas
  const [codigo, setCodigo] = useState('');
  const [modal, setModal] = useState<null | 'buscar' | 'cliente' | 'cobro' | 'suspendidas' | 'recarga' | 'offline' | 'historial'>(null);
  const [ventaSeq, setVentaSeq] = useState(0);  // sube tras cada venta → refresca saldos de fidelización
  const codigoRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const { desktop, fullscreen, activar, salir, toggleFs } = useModoDesktop();

  // Datos de la empresa para el encabezado del ticket térmico (RUC/dirección/teléfono
  // no vienen en el JWT). RLS limita a la empresa del usuario.
  const { user } = useAuth();
  const { data: empresa } = useQuery<EmpresaTicket>({
    queryKey: ['pos-empresa-ticket', user?.empresa?.id],
    enabled: !!user?.empresa?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const rows = (await apiClient.get<EmpresaTicket[]>(`/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,logo,moneda:moneda_funcional`)).data;
      return rows[0] ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket);
    },
  });
  const empresaTicket: EmpresaTicket = empresa ?? ({ nombre: user?.empresa?.nombre, logo: user?.empresa?.logo } as EmpresaTicket);
  const moneda = empresaTicket.moneda ?? 'USD';  // COP sin decimales; USD/PEN con 2
  const cajeroNombre = user?.username ?? null;

  // Cliente por defecto: Consumidor Final (se guarda para restaurarlo tras cada venta)
  const cfRef = useRef<Cliente | null>(null);
  useQuery({ queryKey: ['pos-cf'], queryFn: () => posRpc<Cliente>('pos_consumidor_final', {}).then((cf) => { cfRef.current = cf; setCliente((c) => c ?? cf); return cf; }) });

  const esConsumidorFinal = cliente?.identificacion === '9999999999999';

  // Señal a la cartelería TV: prioriza el historial del cliente apenas se elige
  // (sin esperar a grabar la venta). Consumidor Final → null (vitrina general).
  const señalTvCliente = useCallback((c: Cliente | null) => {
    const id = c && c.identificacion !== '9999999999999' ? c.id : null;
    posRpc('pos_tv_seleccionar_cliente', { p_caja: caja.id, p_cliente: id }).catch(() => {});
  }, [caja.id]);
  // Saldos de fidelización: datos operativos → siempre frescos (el global staleTime es 5 min)
  const { data: puntos } = useQuery({ queryKey: ['pos-fid-saldo', cliente?.id, ventaSeq], enabled: !!cliente && !esConsumidorFinal, queryFn: () => fidelidadSaldo(cliente!.id), staleTime: 0 });
  const { data: nivel } = useQuery({ queryKey: ['pos-fid-nivel', cliente?.id], enabled: !!cliente && !esConsumidorFinal, queryFn: () => fidelidadNivel(cliente!.id), staleTime: 0 });
  const { data: monedero } = useQuery({ queryKey: ['pos-monedero-saldo', cliente?.id, ventaSeq], enabled: !!cliente && !esConsumidorFinal, queryFn: () => monederoSaldo(cliente!.id), staleTime: 0 });
  const { data: sellos = [] } = useQuery<SelloSaldo[]>({ queryKey: ['pos-sellos-saldo', cliente?.id, ventaSeq], enabled: !!cliente && !esConsumidorFinal, queryFn: () => sellosSaldo(cliente!.id), staleTime: 0 });
  const { data: premios = [] } = useQuery<SelloPremio[]>({ queryKey: ['pos-sellos-premios', cliente?.id, ventaSeq], enabled: !!cliente && !esConsumidorFinal, queryFn: () => sellosPremiosDisponibles(cliente!.id), staleTime: 0 });

  const foco = useCallback(() => codigoRef.current?.focus(), []);
  useEffect(() => { if (!modal) foco(); }, [modal, foco]);

  const [promo, setPromo] = useState<PromoEval | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  useEffect(() => {
    if (!lineas.length || tipoDoc === 'nota_venta' || !online) { setPromo(null); setPromoLoading(false); return; }
    let cancel = false; setPromoLoading(true);
    promosEvaluar(cliente?.id ?? null, lineas.map((l) => ({ producto_id: l.producto_id, cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento: l.descuento })), caja.location_id)
      .then((p) => { if (!cancel) setPromo(p); }).catch(() => {}).finally(() => { if (!cancel) setPromoLoading(false); });
    return () => { cancel = true; };
  }, [lineas, cliente, tipoDoc, online, caja.location_id]);
  // mantener el índice seleccionado dentro de rango (F6)
  useEffect(() => { setSel((s) => Math.min(s, Math.max(0, lineas.length - 1))); }, [lineas.length]);
  // Bodega de la caja: nombre (sucursal) + modo de venta (caja|fraccion).
  const { data: bodega } = useQuery({
    queryKey: ['pos-bodega-info', caja.location_id],
    enabled: !!caja.location_id,
    queryFn: () => bodegaInfo(caja.location_id),
  });
  const modoVenta = bodega?.unidad_venta ?? 'caja';
  // Stock en la bodega de la caja para la columna Stock del carrito (1 consulta batch)
  const idsCarrito = useMemo(() => Array.from(new Set(lineas.map((l) => l.producto_id))).sort(), [lineas]);
  // `ok` distingue "cero de verdad" de "no lo sé". Si la consulta falla (o no
  // hay red y el snapshot no cubre el producto), la columna muestra «?» en vez
  // de un cero rojo: un cero falso en todo el carrito hace que el cajero no se
  // atreva a cobrar y esconde la causa real.
  const { data: stockRes } = useQuery({
    queryKey: ['pos-stock-carrito', caja.location_id, idsCarrito.join(','), ventaSeq, online],
    enabled: idsCarrito.length > 0 && !!caja.location_id,
    queryFn: () => (conexion.online
      ? stockProductos(idsCarrito, caja.location_id)
      : stockProductosOffline(idsCarrito, caja.location_id)),
    retry: 1,
    staleTime: 0,
  });
  const stockCarrito = stockRes?.stock ?? {};
  // Sin respuesta todavía, o respuesta incompleta: no afirmamos nada.
  const stockConocido = !!stockRes?.ok;

  // ---- Respaldo de la venta en curso (Ctrl+R / cierre del navegador) ----
  // localStorage al instante (mismo equipo) + borrador en el servidor cada 30s (otro equipo).
  const draftKey = `pos_carrito_${caja.id}`;
  const restauradoRef = useRef(false);
  const dirtyRef = useRef(false);
  const lineasRef = useRef(lineas); lineasRef.current = lineas;
  const clienteRef = useRef(cliente); clienteRef.current = cliente;
  const tipoDocRef = useRef(tipoDoc); tipoDocRef.current = tipoDoc;
  useEffect(() => {
    if (restauradoRef.current) return;
    restauradoRef.current = true;
    const aplicar = (c: { cliente?: Cliente | null; lineas?: Linea[]; tipoDoc?: 'factura' | 'nota_venta' } | null) => {
      if (!c?.lineas?.length) return;
      _k = Math.max(_k, ...c.lineas.map((l) => Number(l.key) || 0)) + 1;
      setLineas(c.lineas);
      if (c.cliente) setCliente(c.cliente);
      if (c.tipoDoc) setTipoDoc(c.tipoDoc);
      toast.info(`Se recuperó una venta en curso (${c.lineas.length} línea${c.lineas.length === 1 ? '' : 's'})`);
    };
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) { aplicar(JSON.parse(raw)); return; }
    } catch { /* respaldo local ilegible: se ignora */ }
    posRpc<{ carrito: { cliente?: Cliente | null; lineas?: Linea[]; tipoDoc?: 'factura' | 'nota_venta' } } | null>('pos_borrador_obtener', { _caja_id: caja.id })
      .then((b) => { if (!lineasRef.current.length) aplicar(b?.carrito ?? null); })
      .catch(() => {});
  }, [caja.id, draftKey]);
  useEffect(() => {
    if (!restauradoRef.current) return;
    dirtyRef.current = true;
    try {
      if (lineas.length) localStorage.setItem(draftKey, JSON.stringify({ cliente, lineas, tipoDoc }));
      else localStorage.removeItem(draftKey);
    } catch { /* cuota llena: el respaldo local es best-effort */ }
  }, [lineas, cliente, tipoDoc, draftKey]);
  useEffect(() => {
    const id = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const carrito = lineasRef.current.length ? { cliente: clienteRef.current, lineas: lineasRef.current, tipoDoc: tipoDocRef.current } : null;
      posRpc('pos_borrador_guardar', { _caja_id: caja.id, _carrito: carrito }).catch(() => { dirtyRef.current = true; });
    }, 30000);
    return () => clearInterval(id);
  }, [caja.id]);
  // Al cobrar/cancelar/suspender: limpiar el respaldo YA (local y servidor), sin esperar el ciclo
  const limpiarRespaldo = useCallback(() => {
    try { localStorage.removeItem(draftKey); } catch { /* best-effort */ }
    dirtyRef.current = false;
    void posRpc('pos_borrador_guardar', { _caja_id: caja.id, _carrito: null }).catch(() => {});
  }, [caja.id, draftKey]);

  // Precio efectivo: en nota de venta el cajero puede fijarlo a mano (override);
  // en factura SIEMPRE el de catálogo (el override se conserva pero se ignora).
  const efPrecio = useCallback((l: Linea) => (tipoDoc === 'nota_venta' && l.precio_manual != null ? l.precio_manual : l.precio_unitario), [tipoDoc]);
  const efDesc = useCallback((l: Linea) => {
    if (tipoDoc === 'nota_venta') return l.precio_manual != null ? 0 : Math.min(l.precio_unitario, l.descuento);
    return Math.min(l.precio_unitario, l.descuento + (promo?.extra?.[l.producto_id] ?? 0) + Math.round(l.precio_unitario * (promo?.global_pct ?? 0) * 10000) / 1000000);
  }, [promo, tipoDoc]);

  const subtotal = useMemo(() => lineas.reduce((s, l) => s + l.cantidad * (efPrecio(l) - efDesc(l)), 0), [lineas, efPrecio, efDesc]);
  const iva = useMemo(() => (tipoDoc === 'nota_venta' ? 0 : lineas.reduce((s, l) => s + l.cantidad * (efPrecio(l) - efDesc(l)) * l.iva_tarifa / 100, 0)), [lineas, efPrecio, efDesc, tipoDoc]);
  // Total redondeado por moneda (subtotal e IVA por separado), espejo de
  // pos_registrar_venta/fac_crear_factura para que el preview cuadre con la venta.
  const total = useMemo(() => redondearMoneda(subtotal, moneda) + redondearMoneda(iva, moneda), [subtotal, iva, moneda]);

  // Formato de la etiqueta de balanza (peso variable) de la empresa (706)
  const { data: cfgBalanza = BALANZA_ETIQUETA_DEFAULT } = useQuery<BalanzaEtiquetaCfg>({
    queryKey: ['pos-balanza-etiqueta'], queryFn: balanzaEtiquetaGet, staleTime: 5 * 60 * 1000,
  });

  // Puente de hardware local (balanza/cajón/display) — opcional, no bloquea nada
  const puente = usePuenteHardware();
  useEffect(() => {
    if (!puente.conectado || !puente.capacidades.display) return;
    const t = setTimeout(() => {
      displayCliente(
        lineas.length ? `TOTAL  $${total.toFixed(2)}` : 'BIENVENIDO',
        lineas.length ? `${lineas.length} item(s)` : '',
      );
    }, 400); // pequeño debounce: no saturar el display en cada tecla
    return () => clearTimeout(t);
  }, [puente.conectado, puente.capacidades.display, total, lineas.length]);
  const lineasVenta = useMemo(() => (tipoDoc === 'nota_venta'
    ? lineas.map((l) => (l.precio_manual != null ? { ...l, precio_unitario: l.precio_manual, descuento: 0 } : l))
    : lineas), [lineas, tipoDoc]);

  // Contexto para las pantallas del local (cross-sell 355 + pantalla de cliente en caja 412):
  // publica el carrito anónimo + payload de exhibición (líneas/totales, SIN datos del cliente).
  // Debounced + fire-and-forget: nunca debe frenar ni afectar la venta.
  useEffect(() => {
    if (!caja.location_id) return;
    const t = setTimeout(() => {
      const ids = lineas.filter((l) => l.producto_id).map((l) => ({ producto_id: l.producto_id, cantidad: l.cantidad }));
      const datos = lineas.length ? {
        estado: 'cobrando',
        lineas: lineasVenta.map((l) => ({ descripcion: l.descripcion, cantidad: l.cantidad, precio: efPrecio(l), subtotal: Math.round(l.cantidad * (efPrecio(l) - efDesc(l)) * 100) / 100 })),
        subtotal: redondearMoneda(subtotal, moneda),
        descuento: Math.round(lineas.reduce((s, l) => s + l.cantidad * efDesc(l), 0) * 100) / 100,
        iva: redondearMoneda(iva, moneda),
        total,
      } : null;
      pushCaja(caja.location_id, caja.id, ids, datos);
    }, 2500);
    return () => clearTimeout(t);
  }, [lineas, lineasVenta, caja.location_id, caja.id, efPrecio, efDesc, subtotal, iva, total, moneda]);

  const recalcDesc = useCallback(async (productoId: string, cantidad: number, precio: number, partnerId?: string | null): Promise<number> => {
    // Espejo EXACTO del servidor: descuento unitario = round(pu*pct/100, 6) — NO redondear
    // a 2 decimales aquí: el server acumula a 6 y redondea el total una sola vez; redondear
    // antes descuadraba el total del preview vs pos_registrar_venta (ej. 168.43 vs 168.45).
    try { const pct = await descuentoVenta(productoId, caja.location_id, cantidad, partnerId !== undefined ? partnerId : (cliente?.id ?? null)); return Math.round(precio * pct * 10000) / 1000000; }
    catch { return 0; }
  }, [caja, cliente]);

  const agregar = useCallback(async (p: ProductoVenta, cantidadInicial?: number) => {
    // cantidadInicial: la usa el código de PESO VARIABLE de balanza (kg leídos de
    // la etiqueta); al repetir el producto se SUMA (cada pesada acumula).
    const q = cantidadInicial ?? 1;
    try {
      const off = !conexion.online;
      const pb = p as Partial<ProductoBusqueda>;
      if (off) {
        // OFFLINE: precio/IVA/descuento del catálogo cacheado; stock del snapshot
        if (p.controla_stock && Number(pb.stock ?? 0) <= 0) {
          toast.error(`"${p.nombre}" sin stock (según el último snapshot) — no se puede agregar`); return;
        }
        const precioOff = Number(p.precio_base) || 0;
        const descOff = Math.round(precioOff * (Number(pb.descuento_pct) || 0) * 10000) / 1000000;
        let cantNueva = q;
        setLineas((ls) => {
          const i = ls.findIndex((x) => x.producto_id === p.id);
          if (i >= 0) { cantNueva = Math.round((ls[i].cantidad + q) * 1000) / 1000; const c = [...ls]; c[i] = { ...c[i], cantidad: cantNueva }; return c; }
          return [...ls, { key: _k++, producto_id: p.id, codigo: p.codigo, descripcion: p.nombre, presentacion: pb.presentacion ?? null, marca: p.marca ?? null, cantidad: q, precio_unitario: precioOff, descuento: descOff, iva_tarifa: Number(p.iva_tarifa) || 0, fracciones: Number(p.fracciones) || 1, controla_stock: p.controla_stock, solo_efectivo: !!pb.solo_efectivo }];
        });
        return;
      }
      if (p.controla_stock && (await stockProducto(p.id, caja.location_id)) <= 0) {
        toast.error(`"${p.nombre}" sin stock en esta bodega — no se puede agregar`); return;
      }
      const pr = await resolverPrecio(p.id, cliente?.id ?? null);
      const f = Number(p.fracciones) || 1;
      const precioCaja = Number(pr.precio) || Number(p.precio_base) || 0;
      // En bodega modo fracción, el precio unitario es por fracción = precio de caja / factor.
      const precio = modoVenta === 'fraccion' && f > 1 ? Math.round(precioCaja / f * 100) / 100 : precioCaja;
      const iva = Number(pr.iva_tarifa ?? p.iva_tarifa) || 0;
      // cantidad correcta vía updater funcional (evita carrera al escanear rápido)
      let nuevaCant = q;
      setLineas((ls) => {
        const i = ls.findIndex((x) => x.producto_id === p.id);
        if (i >= 0) { nuevaCant = Math.round((ls[i].cantidad + q) * 1000) / 1000; const c = [...ls]; c[i] = { ...c[i], cantidad: nuevaCant }; return c; }
        nuevaCant = q;
        return [...ls, { key: _k++, producto_id: p.id, codigo: p.codigo, descripcion: p.nombre, presentacion: pb.presentacion ?? null, marca: p.marca ?? null, cantidad: q, precio_unitario: precio, descuento: 0, iva_tarifa: iva, fracciones: Number(p.fracciones) || 1, controla_stock: p.controla_stock, solo_efectivo: !!pb.solo_efectivo }];
      });
      const desc = await recalcDesc(p.id, nuevaCant, precio);
      setLineas((ls) => ls.map((x) => (x.producto_id === p.id ? { ...x, descuento: desc } : x)));
    } catch (e) { toast.error(getErrorMessage(e)); }
  }, [cliente, recalcDesc, caja, modoVenta]);

  const cambiarCantidad = useCallback(async (l: Linea, cant: number) => {
    // OFFLINE: sin RPC de descuento — se conserva el % cacheado de la línea
    const desc = conexion.online ? await recalcDesc(l.producto_id, cant, l.precio_unitario) : l.descuento;
    setLineas((ls) => ls.map((x) => (x.key === l.key ? { ...x, cantidad: cant, descuento: desc } : x)));
  }, [recalcDesc]);

  const onCliente = useCallback(async (c: Cliente) => {
    setCliente(c); setModal(null);
    señalTvCliente(c);
    const pares = await Promise.all(lineas.map(async (l) => [l.key, await recalcDesc(l.producto_id, l.cantidad, l.precio_unitario, c.id)] as [number, number]));
    const m = new Map(pares);
    setLineas((ls) => ls.map((l) => (m.has(l.key) ? { ...l, descuento: m.get(l.key)! } : l)));
  }, [lineas, recalcDesc, señalTvCliente]);

  const porCodigo = useCallback(async () => {
    const v = codigo.trim(); if (!v) return;
    setCodigo('');
    try {
      // Código de PESO VARIABLE de balanza etiquetadora (formato configurable por
      // empresa, 706): se busca el producto por el PLU y la cantidad de la línea
      // es el PESO en kg — o, si la etiqueta trae PRECIO, importe / PVP.
      const pv = parsearPesoVariable(v, cfgBalanza);
      if (pv) {
        for (const cod of candidatosPlu(pv.plu)) {
          const p = conexion.online
            ? (await buscarProductoPorCodigo(cod))[0]
            : porCodigoEnCatalogo(await catalogoOffline(caja.location_id ?? ''), cod);
          if (p) {
            let cant = pv.pesoKg;
            if (cant == null && pv.importe != null) {
              const pvp = (Number(p.precio_base) || 0) * (1 + (Number(p.iva_tarifa) || 0) / 100);
              if (pvp <= 0) { toast.error(`"${p.nombre}" sin precio — no se puede derivar la cantidad del importe`); return; }
              cant = Math.round((pv.importe / pvp) * 1000) / 1000;
            }
            if (!cant || cant <= 0) { toast.error('Etiqueta de balanza sin peso/importe válido'); return; }
            await agregar(p, cant);
            toast.success(`⚖ ${p.nombre}: ${cant.toFixed(3)} kg (etiqueta de balanza)`);
            return;
          }
        }
        toast.error(`Etiqueta de balanza: ningún producto con PLU ${pv.plu}`);
        return;
      }
      if (!conexion.online) {
        const p = porCodigoEnCatalogo(await catalogoOffline(caja.location_id ?? ''), v);
        if (p) await agregar(p); else toast.error(`Sin coincidencia (catálogo offline): ${v}`);
        return;
      }
      const r = await buscarProductoPorCodigo(v); if (r.length) await agregar(r[0]); else toast.error(`Sin coincidencia: ${v}`);
    }
    catch (e) { toast.error(getErrorMessage(e)); }
  }, [codigo, agregar, caja.location_id, cfgBalanza]);

  const quitarSel = useCallback(() => setLineas((ls) => ls.filter((_, i) => i !== sel)), [sel]);
  const cancelar = useCallback(async () => { if (lineas.length && await dlg.confirmar('¿Cancelar toda la transacción?', { peligro: true })) { setLineas([]); setTipoDoc('factura'); limpiarRespaldo(); } }, [lineas, limpiarRespaldo, dlg]);

  // Teclas funcionales (solo en la pantalla principal)
  useEffect(() => {
    if (modal) return;
    const h = (e: KeyboardEvent) => {
      if (!esVisible(rootRef.current)) return;  // pestaña oculta (keep-alive); válido también en modo fixed/kiosko
      const enCodigo = document.activeElement === codigoRef.current;
      const elAct = document.activeElement as HTMLElement | null;
      const enInput = !!elAct && ['INPUT', 'TEXTAREA', 'SELECT'].includes(elAct.tagName);
      // input numérico de una línea del carrito (cantidad o precio manual)
      const enFilaNum = enInput && (elAct as HTMLInputElement).type === 'number';
      // Confirma en la línea seleccionada la cantidad que se venía tecleando (recalcula descuento).
      const commitQty = () => setQtyBuf((buf) => { if (buf !== '' && lineas[sel]) void cambiarCantidad(lineas[sel], Math.max(0, Number(buf) || 0)); return ''; });
      if (e.key === 'F1') { e.preventDefault(); setModal('buscar'); }
      else if (e.key === 'F2') { e.preventDefault(); if (cliente) setModal('historial'); else toast.info('Selecciona un cliente (F5) para ver su historial de compras'); }
      else if (e.key === 'F4') { e.preventDefault(); setModal('recarga'); }
      else if (e.key === 'F5') { e.preventDefault(); setModal('cliente'); }
      else if (e.key === 'F6') { e.preventDefault(); quitarSel(); }
      else if (e.key === 'F7') { e.preventDefault(); setTipoDoc('factura'); }
      else if (e.key === 'F8') { e.preventDefault(); if (caja.permite_nota_venta !== false) setTipoDoc('nota_venta'); }
      else if (e.key === 'F9') { e.preventDefault(); void cancelar(); }
      else if (e.key === 'F10') { e.preventDefault(); setModal('suspendidas'); }
      else if (e.code === 'Space') {
        // Espacio = Cobrar. Funciona escaneando (código vacío), navegando líneas (foco en
        // body) o editando la cantidad de una línea. Solo se bloquea mientras se teclea un
        // código CON contenido (ahí el espacio se escribe en el campo, no cobra).
        const tecleandoCodigo = enCodigo && !!codigo;
        if (!tecleandoCodigo) { e.preventDefault(); if (lineas.length && !promoLoading) setModal('cobro'); }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Navegar líneas: confirma la cantidad tecleada y pasa de línea. Desenfoca el
        // código → modo edición de líneas (los dígitos van a la cantidad, no al escáner).
        e.preventDefault();
        commitQty();
        codigoRef.current?.blur();
        setSel((s) => (e.key === 'ArrowDown' ? Math.min(s + 1, lineas.length - 1) : Math.max(s - 1, 0)));
      } else if (!enInput && lineas.length > 0 && /^[0-9]$/.test(e.key)) {
        // Teclear un número fija la cantidad de la línea seleccionada (acumula dígitos).
        e.preventDefault();
        const k = lineas[sel]?.key;
        if (k != null) setQtyBuf((buf) => { const nb = (buf + e.key).replace(/^0+(?=\d)/, ''); const n = Math.max(0, Number(nb) || 0); setLineas((ls) => ls.map((x) => (x.key === k ? { ...x, cantidad: n } : x))); return nb; });
      } else if (!enInput && lineas.length > 0 && (e.key === '.' || e.key === ',')) {
        e.preventDefault();
        setQtyBuf((buf) => (buf.includes('.') ? buf : buf === '' ? '0.' : buf + '.'));
      } else if (!enInput && e.key === 'Backspace') {
        e.preventDefault();
        const k = lineas[sel]?.key;
        setQtyBuf((buf) => { const nb = buf.slice(0, -1); if (k != null) setLineas((ls) => ls.map((x) => (x.key === k ? { ...x, cantidad: Math.max(0, Number(nb) || 0) } : x))); return nb; });
      } else if ((!enInput || enFilaNum) && e.key === 'Enter') {
        // Confirma la cantidad y vuelve al modo escaneo (foco al campo de código).
        e.preventDefault();
        commitQty();
        foco();
      } else if (e.key === '+' || e.key === 'Add') {
        if ((enCodigo && !codigo) || document.activeElement === document.body) { e.preventDefault(); commitQty(); const l = lineas[sel]; if (l) void cambiarCantidad(l, l.cantidad + 1); }
      } else if (e.key === '-' || e.key === 'Subtract') {
        if ((enCodigo && !codigo) || document.activeElement === document.body) { e.preventDefault(); commitQty(); const l = lineas[sel]; if (l) void cambiarCantidad(l, Math.max(1, l.cantidad - 1)); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [modal, lineas, sel, codigo, quitarSel, cancelar, cambiarCantidad, promoLoading, foco, cliente, caja.permite_nota_venta]);

  // Venta confirmada: imprime el ticket y deja la pantalla lista para la siguiente
  // venta. Caja en modo 'red' → ESC/POS directo (sin diálogo) vía el print-agent;
  // si no se pudo encolar (o falla la llamada), cae al modo terminal (navegador)
  // para que la venta SIEMPRE imprima.
  const onVendida = async (t: TicketData) => {
    const tk = {
      codigo: t.codigo, tipoDoc: t.tipoDoc, claveAcceso: t.claveAcceso, serie: t.serie, caja: t.caja, cajero: cajeroNombre, cliente: t.cliente, lineas: t.lineas,
      subtotal: t.subtotal, iva: t.iva, total: t.total, recibido: t.recibido, vuelto: t.vuelto,
      pagos: t.pagos, puntosGanados: t.puntosGanados, puntosSaldo: t.puntosSaldo,
      cashbackGanado: t.cashbackGanado, monederoSaldo: t.monederoSaldo, sellos: t.sellos, habitacion: t.habitacion,
    };
    if (caja.modo_impresion === 'red') {
      try {
        // El print-agent (ESC/POS) no resuelve tokens ni fuentes de QR: recibe los
        // bloques del encabezado/pie YA resueltos (mismo helper que usa el HTML).
        const cfg = configVigente();
        const ctx = { empresa: empresaTicket, ticket: tk };
        const payload = {
          ...tk, empresa: empresaTicket,
          encabezado_bloques: resolverBloquesParaImpresion(cfg.encabezado_bloques, ctx),
          pie_bloques: resolverBloquesParaImpresion(cfg.pie_bloques, ctx),
          avance_final: cfg.avance_final,
          abrir_cajon: debeAbrirCajon(cfg, tk.pagos),
        };
        const r = await posTicketImprimir(caja.id, payload);
        if (!r.encolado) {
          toast.warning(r.motivo ?? 'No se pudo enviar a la impresora de red');
          await imprimirTicket(tk, empresaTicket);
        }
      } catch (e) {
        toast.warning(getErrorMessage(e) || 'No se pudo enviar a la impresora de red');
        await imprimirTicket(tk, empresaTicket);
      }
    } else {
      await imprimirTicket(tk, empresaTicket);
    }
    setLineas([]); setTipoDoc('factura'); limpiarRespaldo(); setModal(null); señalTvCliente(null); setVentaSeq((n) => n + 1);
    setCliente(cfRef.current);  // tras grabar, vuelve a CONSUMIDOR FINAL para la próxima venta
  };

  return (
    <div ref={rootRef} className={desktop ? 'fixed inset-0 z-40 flex h-screen flex-col bg-background' : 'flex h-[calc(100vh-3.5rem)] flex-col'}>
      {dlg.abierto && <div className="shrink-0 border-b p-3">{dlg.ui}</div>}
      {/* Barra superior */}
      <div className="flex items-center justify-between border-b px-4 py-2 text-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-extrabold uppercase tracking-wide text-primary">{bodega?.nombre || caja.nombre}</span>
            {caja.establecimiento && (
              <span className="rounded-md bg-primary px-2 py-0.5 text-base font-bold tabular-nums text-primary-foreground">
                {caja.establecimiento}-{caja.punto_emision ?? '000'}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{caja.nombre} · Turno {sesion.codigo}</span>
          <button onClick={() => setModal('offline')}
            title={online ? `En línea · ${bloqueRestante} claves offline en reserva` : 'SIN CONEXIÓN: ventas en cola local, se sincronizan al volver la red'}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${online
              ? (pendOffline > 0 ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300')
              : 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 animate-pulse'}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${online ? (pendOffline > 0 ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-red-500'}`} />
            {online ? (pendOffline > 0 ? `Sincronizando (${pendOffline})` : 'En línea') : `OFFLINE${pendOffline ? ` · ${pendOffline}` : ''}`}
          </button>
          <div className="flex items-center gap-1">
            <button title="Abrir cajón de dinero" className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted" onClick={() => abrirCajon()}>
              <Banknote className="h-4 w-4" /> Abrir cajón
            </button>
            <button title={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'} className="rounded-md border p-1.5 hover:bg-muted" onClick={toggleFs}>
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {desktop
              ? <button title="Salir del modo desktop (vuelve al ERP)" className="rounded-md border px-2 py-1 text-xs hover:bg-muted" onClick={salir}>Salir</button>
              : <button title="Modo desktop (pantalla completa, kiosko)" className="rounded-md border px-2 py-1 text-xs hover:bg-muted" onClick={activar}>Modo desktop</button>}
          </div>
        </div>
        <button className="flex items-center gap-2 rounded-md border px-3 py-1 hover:bg-muted" onClick={() => setModal('cliente')}>
          <span className="text-xs text-muted-foreground">Cliente (F5):</span>
          <span className="font-medium">{cliente?.razon_social ?? '—'}</span>
          {!esConsumidorFinal && nivel?.nombre && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{nivel.nombre}</span>}
          {!esConsumidorFinal && puntos != null && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{Number(puntos).toLocaleString(LOCALE)} pts</span>}
          {!esConsumidorFinal && monedero != null && Number(monedero) > 0 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Monedero {money(Number(monedero), moneda)}</span>}
          {!esConsumidorFinal && sellos.length > 0 && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" title={sellos.map((s) => `${s.nombre}: ${s.saldo}/${s.meta}`).join('\n')}>Sellos {sellos.map((s) => `${s.saldo}/${s.meta}`).join(' · ')}</span>}
          {!esConsumidorFinal && premios.length > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">{premios.length} premio{premios.length > 1 ? 's' : ''} 🎁</span>}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Columna principal */}
        <div className="flex flex-1 flex-col p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <input ref={codigoRef} autoFocus className={`${fieldCls} h-11 pl-9 text-base`} placeholder="Escanea o teclea el código y Enter · F1 para buscar por nombre"
              value={codigo} onChange={(e) => setCodigo(e.target.value)} onFocus={() => setCodigoFocus(true)} onBlur={() => setCodigoFocus(false)} onKeyDown={(e) => { if (e.key === 'Enter') porCodigo(); }} />
          </div>
          <Card className="flex-1 overflow-auto">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-20 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 px-3">Código</th><th className="py-2 px-3">Descripción</th>
                  <th className="py-2 px-3 w-16 text-right">Fracc.</th><th className="py-2 px-3 w-20 text-right">{modoVenta === 'fraccion' ? 'Stock (u)' : 'Stock (cajas)'}</th>
                  <th className="py-2 px-3 w-24 text-right">Cant.</th><th className="py-2 px-3 w-28 text-right">Precio</th>
                  <th className="py-2 px-3 w-28 text-right" title="Precio unitario con IVA incluido">P. + IVA</th>
                  <th className="py-2 px-3 w-32 text-right">Desc. ($/%)</th><th className="py-2 px-3 w-28 text-right" title="Total de la línea con IVA incluido">Total</th><th className="w-10"></th>
                </tr></thead>
                <tbody>
                  {lineas.length === 0 && <tr><td colSpan={10} className="py-12 text-center text-muted-foreground">Escanea o busca un producto (F1)</td></tr>}
                  {lineas.map((l, i) => {
                    const desc = efDesc(l);
                    const pct = l.precio_unitario > 0 ? (desc / l.precio_unitario) * 100 : 0;
                    const editandoCant = !codigoFocus && i === sel;  // edición por teclado de esta línea
                    return (
                    <tr key={l.key} className={`border-b last:border-0 ${navRowCls(i === sel)}`} onClick={() => { setSel(i); setQtyBuf(''); codigoRef.current?.blur(); }}>
                      <td className="py-1.5 px-3 font-mono text-xs">{l.codigo}</td>
                      <td className="py-1.5 px-3">
                        <div className="truncate">{l.descripcion}</div>
                        {/* la marca es SOLO informativa en pantalla: no viaja a la descripción ni al ticket */}
                        {l.marca && <div className="truncate text-[11px] text-muted-foreground">{l.marca}</div>}
                        {l.presentacion && <div className="truncate text-[11px] text-muted-foreground">{l.presentacion}</div>}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-muted-foreground">{Number(l.fracciones) || 1}</td>
                      {(() => {
                        const f = Number(l.fracciones) || 1;
                        const sinControl = l.controla_stock === false;
                        // Dato ausente ≠ dato cero. Sólo se afirma un número si
                        // la consulta respondió; si no, «?» y sin rojo.
                        const crudo = stockCarrito[l.producto_id];
                        const desconocido = !sinControl && crudo === undefined && !stockConocido;
                        const stFrac = crudo ?? 0;   // stock en fracciones (base)
                        const stDisp = modoVenta === 'fraccion' ? stFrac : Math.floor(stFrac / f);   // en la unidad de la bodega
                        const necesitaFrac = l.cantidad * (modoVenta === 'fraccion' ? 1 : f);
                        const insuf = !desconocido && stFrac < necesitaFrac;
                        return (
                        <td className={`py-1.5 px-3 text-right tabular-nums ${sinControl || desconocido ? 'text-muted-foreground' : insuf ? 'font-semibold text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}
                            title={sinControl ? 'No controla stock'
                              : desconocido ? 'No se pudo consultar el stock de esta bodega (sin respuesta del servidor). El cobro no está bloqueado por esto: el stock se valida al registrar la venta.'
                              : `Stock en la bodega (${modoVenta === 'fraccion' ? 'unidades sueltas' : 'cajas'})${insuf ? ' — insuficiente para la cantidad' : ''}`}>
                          {sinControl ? '—' : desconocido ? '?' : stDisp.toLocaleString(LOCALE)}
                        </td>); })()}
                      <td className={`py-1.5 px-3 ${editandoCant ? 'bg-primary/15' : ''}`}>
                        <input type="number" step="0.01" min="0"
                          className={`${fieldCls} h-8 text-right ${editandoCant ? 'ring-2 ring-primary border-primary font-semibold' : ''}`}
                          value={l.cantidad} onChange={(e) => cambiarCantidad(l, Math.max(0, Number(e.target.value) || 0))} /></td>
                      <td className="py-1.5 px-3 text-right tabular-nums">
                        {tipoDoc === 'nota_venta' ? (
                          <input type="number" step="0.01" min="0"
                            className={`${fieldCls} h-8 w-24 ml-auto text-right ${l.precio_manual != null ? 'border-amber-500 ring-1 ring-amber-500' : ''}`}
                            title={l.precio_manual != null ? `Precio manual — catálogo: ${money(l.precio_unitario, moneda)} (borra el valor para volver al catálogo)` : 'Precio editable (nota de venta)'}
                            value={l.precio_manual ?? l.precio_unitario}
                            onClick={(ev) => ev.stopPropagation()}
                            onFocus={() => setQtyBuf('')}
                            onChange={(e) => { const raw = e.target.value; setLineas((ls) => ls.map((x) => (x.key === l.key ? { ...x, precio_manual: raw === '' ? null : Math.max(0, Number(raw) || 0) } : x))); }} />
                        ) : money(l.precio_unitario, moneda)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-muted-foreground"
                          title={`Precio unitario con IVA ${Number(l.iva_tarifa) || 0}% incluido`}>
                        {money(efPrecio(l) * (1 + (Number(l.iva_tarifa) || 0) / 100), moneda)}
                      </td>
                      <td className="py-1.5 px-3 text-right text-muted-foreground tabular-nums" title="Descuento por unidad (bodega/cliente + promociones)">
                        {desc > 0 ? <>{money(desc, moneda)} <span className="text-xs">({pct.toLocaleString(LOCALE, { maximumFractionDigits: 2 })}%)</span></> : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right font-medium tabular-nums" title={`Con IVA ${Number(l.iva_tarifa) || 0}% incluido`}>{money(l.cantidad * (efPrecio(l) - desc) * (1 + (Number(l.iva_tarifa) || 0) / 100), moneda)}</td>
                      <td className="py-1.5 px-2 text-right"><button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={(ev) => { ev.stopPropagation(); setLineas((ls) => ls.filter((x) => x.key !== l.key)); }}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Panel lateral: totales + acciones */}
        <div className="w-72 border-l p-4 flex flex-col gap-3">
          <div className="rounded-lg border p-4 space-y-1">
            <Row k="Subtotal" v={money(subtotal, moneda)} />
            <Row k="IVA" v={money(iva, moneda)} />
            <div className="border-t pt-2 flex justify-between text-xl font-bold"><span>Total</span><span>{money(total, moneda)}</span></div>
          </div>
          {/* Balanza del puente de hardware local (print-agent Fase 2): peso en
              vivo + un clic lo pone como CANTIDAD de la línea seleccionada. */}
          {puente.conectado && puente.capacidades.balanza && (
            <div className="rounded-lg border border-sky-300 bg-sky-50 p-2 text-sm dark:border-sky-800 dark:bg-sky-900/15 flex items-center justify-between gap-2">
              <span className="font-mono tabular-nums">
                ⚖ {puente.peso != null ? `${puente.peso.toFixed(3)} kg` : '—'}
                {puente.peso != null && !puente.estable && <span className="ml-1 text-xs text-amber-600">(inestable)</span>}
              </span>
              <Button size="sm" variant="outline"
                disabled={puente.peso == null || puente.peso <= 0 || !lineas[sel]}
                title="Usar el peso de la balanza como cantidad de la línea seleccionada"
                onClick={() => { const l = lineas[sel]; if (l && puente.peso) void cambiarCantidad(l, Number(puente.peso.toFixed(3))); }}>
                Usar peso → cantidad
              </Button>
            </div>
          )}
          {promo && promo.aplicadas?.length > 0 && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2 text-xs dark:bg-emerald-900/15">
              <div className="mb-1 font-medium text-emerald-700 dark:text-emerald-300">Promociones aplicadas</div>
              {promo.aplicadas.map((a, i) => <div key={i}>• {a.nombre}</div>)}
              {promo.regalos?.length > 0 && <div className="mt-1 text-emerald-700 dark:text-emerald-300">+ {promo.regalos.length} regalo(s) (se agregan al confirmar)</div>}
            </div>
          )}
          {caja.permite_nota_venta === false ? (
            <div className="rounded-md border py-1.5 text-center text-sm font-medium bg-primary text-primary-foreground" title="Esta caja solo emite Facturas">Factura (F7)</div>
          ) : (
            <div className="grid grid-cols-2 overflow-hidden rounded-md border text-sm" title="F7 factura · F8 nota de venta">
              <button className={`py-1.5 ${tipoDoc === 'factura' ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`} onClick={() => setTipoDoc('factura')}>Factura (F7)</button>
              <button className={`py-1.5 ${tipoDoc === 'nota_venta' ? 'bg-amber-500 text-white font-medium' : 'hover:bg-muted'}`} onClick={() => setTipoDoc('nota_venta')}>Nota venta (F8)</button>
            </div>
          )}
          <Button className="h-12 text-base" disabled={!lineas.length || promoLoading} onClick={() => setModal('cobro')}>Cobrar (Espacio)</Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setModal('buscar')}>Buscar (F1)</Button>
            <Button variant="outline" onClick={() => setModal('cliente')}>Cliente (F5)</Button>
            <Button variant="outline" onClick={() => { if (cliente) setModal('historial'); else toast.info('Selecciona un cliente (F5) primero'); }}>Historial (F2)</Button>
            <Button variant="outline" onClick={quitarSel} disabled={!lineas.length}>Quitar (F6)</Button>
            <Button variant="outline" onClick={cancelar} disabled={!lineas.length}>Cancelar (F9)</Button>
            <Button variant="outline" onClick={() => setModal('recarga')}>Recargas (F4)</Button>
            <Button variant="outline" onClick={() => setModal('suspendidas')}>Suspender (F10)</Button>
          </div>
          <p className="mt-auto text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium">Teclado:</span> <kbd className="rounded border px-1">↑</kbd>/<kbd className="rounded border px-1">↓</kbd> selecciona la línea · teclea un <span className="font-medium">número</span> para fijar la cantidad (varios dígitos) · <kbd className="rounded border px-1">Enter</kbd> confirma · <kbd className="rounded border px-1">+</kbd>/<kbd className="rounded border px-1">−</kbd> ajustan de a uno.
          </p>
        </div>
      </div>

      {modal === 'offline' && <ColaOffline online={online} onSync={() => { void sincronizarVentas().then(() => refrescarPend()); }} onChange={refrescarPend} onClose={() => setModal(null)} />}
      {modal === 'buscar' && <BuscarProducto location={caja.location_id} partner={cliente?.id ?? null} moneda={moneda} modoVenta={modoVenta} onPick={(p) => { setModal(null); agregar(p); }} onClose={() => setModal(null)} />}
      {modal === 'recarga' && <RecargaBroadnet caja={caja} cliente={cliente} moneda={moneda} emp={empresaTicket} cajero={cajeroNombre} onClose={() => setModal(null)} />}
      {modal === 'cliente' && <BuscarCliente onPick={onCliente} onClose={() => setModal(null)} />}
      {modal === 'historial' && cliente && <HistorialCliente cliente={cliente} moneda={moneda} onClose={() => setModal(null)} />}
      {modal === 'cobro' && cliente && <Cobro caja={caja} cliente={cliente} lineas={lineasVenta} total={total} moneda={moneda} tipoDoc={tipoDoc} puntos={esConsumidorFinal ? 0 : (puntos ?? 0)} monedero={esConsumidorFinal ? 0 : (monedero ?? 0)} premios={esConsumidorFinal ? [] : premios} onClose={() => setModal(null)} onVendida={onVendida} />}
      {modal === 'suspendidas' && (
        <Suspendidas caja={caja} cliente={cliente} lineas={lineas} total={total} moneda={moneda}
          onSuspendida={() => { setLineas([]); limpiarRespaldo(); setModal(null); }}
          onRetomar={(c) => { setCliente(c.cliente); setLineas(c.lineas); setModal(null); señalTvCliente(c.cliente); }}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) { return <div className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>; }

// ---- Modal genérico ----
// Pila de modales abiertos: Esc cierra SOLO el de más arriba (último montado).
const modalStack: symbol[] = [];
function ModalBox({ title, onClose, children, wide, xwide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean; xwide?: boolean }) {
  const idRef = useRef<symbol>();
  if (!idRef.current) idRef.current = Symbol('modal');
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(() => {
    const id = idRef.current!;
    modalStack.push(id);
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== id) return;  // solo el de arriba
      e.preventDefault(); e.stopPropagation(); onCloseRef.current();
    };
    window.addEventListener('keydown', h);
    return () => { window.removeEventListener('keydown', h); const i = modalStack.lastIndexOf(id); if (i >= 0) modalStack.splice(i, 1); };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className={`mt-10 w-full ${xwide ? 'max-w-6xl' : wide ? 'max-w-3xl' : 'max-w-lg'} rounded-lg border bg-card shadow-lg`}>
        <div className="flex items-center justify-between border-b px-4 py-3"><h3 className="font-semibold">{title}</h3><button onClick={onClose} className="rounded p-1 hover:bg-muted"><X className="h-4 w-4" /></button></div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ---- F1: buscar producto por nombre ----
function BuscarProducto({ location, partner, moneda, modoVenta, onPick, onClose }: { location: string | null; partner: string | null; moneda: string; modoVenta: string; onPick: (p: ProductoBusqueda) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');  // texto debounced (evita 1 request por tecla)
  useEffect(() => { const t = setTimeout(() => setQd(q), 250); return () => clearTimeout(t); }, [q]);
  const [marcaF, setMarcaF] = useState<{ id: string; nombre: string } | null>(null);  // filtro por marca (F8)
  const [pa, setPa] = useState<string | null>(null);                            // equivalentes por principio activo (F9)
  const [catF, setCatF] = useState<CategoriaVenta | null>(null);                // botones dinámicos de familia
  const { data: categorias = [] } = useQuery<CategoriaVenta[]>({
    queryKey: ['pos-categorias-venta'], queryFn: categoriasVenta, staleTime: 5 * 60 * 1000,
  });
  const { data: res = [], isFetching } = useQuery<ProductoBusqueda[]>({
    queryKey: ['pos-buscar-prod', qd, location, partner, marcaF?.id ?? null, pa ?? null, catF?.id ?? null],
    enabled: catF || marcaF || pa ? true : qd.trim().length >= 2,
    queryFn: async () => {
      if (!conexion.online) {
        // OFFLINE: filtra el catálogo cacheado (snapshot del último momento con red)
        return buscarEnCatalogo(await catalogoOffline(location ?? ''), qd, catF?.id, marcaF?.id, pa);
      }
      return catF ? buscarProductosPorCategoria(catF.id, location, partner)
        : marcaF ? buscarProductosPorMarca(marcaF.id, location, partner)
        : pa ? buscarProductosPorPrincipio(pa, location, partner)
        : buscarProductosPos(qd, location, partner);
    },
    staleTime: 0,  // el stock mostrado debe ser fresco
  });
  // Ordenamiento por columna (asc/desc); por defecto respeta el orden del backend
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (k: string) => { if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSortKey(k); setSortDir('asc'); } };
  // Precio efectivo según el modo de la bodega: por fracción = precio de caja / factor.
  const esFrac = (p: ProductoBusqueda) => modoVenta === 'fraccion' && (Number(p.fracciones) || 1) > 1;
  const precioCaja = (p: ProductoBusqueda) => Number(p.precio_base) || 0;
  const precioUnit = (p: ProductoBusqueda) => esFrac(p) ? Math.round(precioCaja(p) / (Number(p.fracciones) || 1) * 100) / 100 : precioCaja(p);
  const finalDe = (p: ProductoBusqueda) => {
    const precio = precioUnit(p);
    const base = precio - Math.round(precio * (Number(p.descuento_pct) || 0) * 10000) / 1000000;
    return Math.round((base + base * (Number(p.iva_tarifa) || 0) / 100) * 100) / 100;
  };
  const sinStk = (p: ProductoBusqueda) => p.controla_stock && Number(p.stock) <= 0;
  const vista = useMemo(() => {
    const val = (p: ProductoBusqueda): string | number => {
      switch (sortKey) {
        case 'codigo': return p.codigo ?? '';
        case 'nombre': return p.nombre ?? '';
        case 'present': return p.presentacion ?? '';
        case 'fracc': return Number(p.fracciones) || 1;
        case 'marca': return p.marca ?? '';
        case 'stock': return Number(p.stock);
        case 'precio': return precioUnit(p);
        case 'desc': return Number(p.descuento_pct) || 0;
        case 'final': return finalDe(p);
        default: return '';
      }
    };
    const arr = [...res];
    arr.sort((a, b) => {
      // Siempre: los que tienen stock, primero.
      const g = (sinStk(a) ? 1 : 0) - (sinStk(b) ? 1 : 0);
      if (g !== 0) return g;
      if (!sortKey) return 0;   // dentro del grupo, respeta el orden del backend
      const va = val(a); const vb = val(b);
      const c = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? c : -c;
    });
    return arr;
  }, [res, sortKey, sortDir, modoVenta]);

  // Navegación por teclado: ↑/↓ mueven la selección, Enter agrega el resaltado
  const [idx, setIdx] = useState(0);
  const selRef = useRef<HTMLTableRowElement>(null);
  // Cada vez que cambian las opciones visibles (nueva búsqueda, filtro u orden),
  // resalta SIEMPRE la primera fila, para navegar con ↑/↓ desde arriba.
  const vistaSig = vista.length ? `${vista.length}:${vista[0].id}` : '';
  useEffect(() => { setIdx(0); }, [vistaSig, sortKey, sortDir]);
  useEffect(() => { selRef.current?.scrollIntoView({ block: 'nearest' }); }, [idx]);
  const [detalle, setDetalle] = useState<{ tipo: 'stock' | 'kardex'; prod: ProductoBusqueda } | null>(null);
  const sinStock = (p: ProductoBusqueda) => p.controla_stock && Number(p.stock) <= 0;
  // Semáforo de stock contra el mín/máx DINÁMICO (motor de Reposición, script 301):
  // 🔴 agotado o ≤ mínimo · 🟠 ≤ mín + 25% del rango · 🟡 ≤ 75% · 🟢 arriba. Sin mínimo = neutro.
  const stockSemaforo = (p: ProductoBusqueda): { cls: string; nivel: string } => {
    if (!p.controla_stock) return { cls: 'text-muted-foreground', nivel: 'no controla stock' };
    const st = Number(p.stock), min = Number(p.stock_min), max = Number(p.stock_max);
    if (st <= 0) return { cls: 'text-red-600 dark:text-red-400 font-semibold', nivel: 'AGOTADO' };
    if (min <= 0) return { cls: '', nivel: 'sin mínimo definido' };
    if (st <= min) return { cls: 'text-red-600 dark:text-red-400 font-semibold', nivel: 'BAJO MÍNIMO — reponer ya' };
    const pos = (st - min) / Math.max(max - min, 1);
    if (pos <= 0.25) return { cls: 'text-orange-600 dark:text-orange-400 font-medium', nivel: 'cerca del mínimo' };
    if (pos <= 0.75) return { cls: 'text-amber-600 dark:text-amber-500', nivel: 'nivel medio' };
    return { cls: 'text-emerald-600 dark:text-emerald-400', nivel: 'nivel sano' };
  };
  const stockTitle = (p: ProductoBusqueda) => {
    const sem = stockSemaforo(p);
    const base = Number(p.stock_min) > 0
      ? `Stock ${Number(p.stock)} · mín ${Number(p.stock_min)} · máx ${Number(p.stock_max)} — ${sem.nivel}`
      : `Stock ${Number(p.stock)} — ${sem.nivel}`;
    return Number(p.transito_entrante ?? 0) > 0
      ? `${base} · EN CAMINO: +${Number(p.transito_entrante)}${p.transito_eta ? ` (llega ~${p.transito_eta})` : ''} — no vendible hasta recibirse`
      : base;
  };
  const pick = (p: ProductoBusqueda) => {
    if (sinStock(p)) { toast.error(`"${p.nombre}" sin stock en esta bodega — no se puede agregar`); return; }
    onPick(p);
  };
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, vista.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (vista[idx]) pick(vista[idx]); }
    else if (e.key === 'F2') { e.preventDefault(); setQ(''); setMarcaF(null); setPa(null); setCatF(null); }
    else if (e.key === 'F4') { e.preventDefault(); if (vista[idx]) setDetalle({ tipo: 'stock', prod: vista[idx] }); }
    else if (e.key === 'F5') { e.preventDefault(); if (vista[idx]) setDetalle({ tipo: 'kardex', prod: vista[idx] }); }
    else if (e.key === 'F8') {
      e.preventDefault();
      const p = vista[idx];
      if (p?.marca_id) { setPa(null); setCatF(null); setMarcaF({ id: p.marca_id, nombre: p.marca ?? 'Marca' }); }
      else if (p) toast.error('El producto no tiene marca asignada');
    }
    else if (e.key === 'F9') {
      e.preventDefault();
      const p = vista[idx];
      if (p?.principio_activo) { setMarcaF(null); setCatF(null); setPa(p.principio_activo); }
      else if (p) toast.error('El producto no tiene principio activo asignado');
    }
  };
  // Thumbnails: consulta batch aparte (no bloquea ni ralentiza la búsqueda principal)
  const ids = useMemo(() => res.map((p) => p.id), [res]);
  const { data: thumbs = {} } = useQuery<Record<string, string>>({
    queryKey: ['pos-prod-thumbs', ids.join(',')], enabled: ids.length > 0,
    queryFn: () => productoThumbs(ids), staleTime: 5 * 60 * 1000,
  });
  // sticky va en cada <th> (en <thead> no funciona en Chrome) + fondo para tapar las filas al pasar
  const th = 'sticky top-0 z-20 bg-card py-2 px-2 text-xs font-medium text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]';
  const Th = ({ col, children, right = false }: { col: string; children: React.ReactNode; right?: boolean }) => (
    <th className={`${th} ${right ? 'text-right' : 'text-left'} cursor-pointer select-none hover:text-foreground`} onClick={() => toggleSort(col)} title="Ordenar">
      <span className={`inline-flex items-center gap-0.5 ${right ? 'flex-row-reverse' : ''}`}>
        {children}
        <span className="text-[10px]">{sortKey === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </span>
    </th>
  );
  const ORIGEN_LABEL: Record<string, string> = { producto: 'producto', familia: 'familia', cliente: 'cliente', mayorista: 'MAYORISTA', markdown: 'liquidación' };
  const sel = vista[idx] ?? null;
  const { data: imagenSel } = useQuery<string | null>({
    queryKey: ['pos-prod-imagen', sel?.id ?? ''],
    enabled: !!sel,
    queryFn: () => productoImagen(sel!.id),
    staleTime: 5 * 60 * 1000,
  });
  // Refresca el catálogo visible sin recargar la página: resultados, categorías,
  // miniaturas e imagen de la vista previa (todas las keys pos-*).
  const qcSearch = useQueryClient();
  const refrescarCatalogo = () => {
    void qcSearch.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('pos-'),
    });
    toast.success('Catálogo actualizado');
  };
  return (
    <ModalBox title="Buscar producto (F1)" onClose={onClose} xwide>
      <div className="flex gap-2">
        <input autoFocus className={fieldCls} placeholder="Nombre del producto…" value={q} onChange={(e) => { setQ(e.target.value); setMarcaF(null); setPa(null); setCatF(null); }} onKeyDown={onKey} />
        <button
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground"
          title="Actualizar el catálogo (productos, precios, stock, categorías e imágenes)"
          onClick={refrescarCatalogo} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>
      {categorias.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {categorias.map((c) => (
            <button key={c.id}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${catF?.id === c.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground'}`}
              onClick={() => { setMarcaF(null); setPa(null); setCatF((cur) => (cur?.id === c.id ? null : c)); }}>
              {c.nombre}
            </button>
          ))}
        </div>
      )}
      {catF && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm dark:border-amber-900/40 dark:bg-amber-900/15">
          <span className="text-amber-700 dark:text-amber-300">Familia: <span className="font-semibold">{catF.nombre}</span> ({vista.length} productos)</span>
          <button className="ml-auto rounded px-2 py-0.5 text-xs text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30" onClick={() => setCatF(null)}>× Quitar filtro</button>
        </div>
      )}
      {marcaF && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm dark:border-indigo-900/40 dark:bg-indigo-900/15">
          <span className="text-indigo-700 dark:text-indigo-300">Productos de la marca: <span className="font-semibold">{marcaF.nombre}</span></span>
          <button className="ml-auto rounded px-2 py-0.5 text-xs text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-900/30" onClick={() => setMarcaF(null)}>× Quitar filtro</button>
        </div>
      )}
      {pa && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-teal-300 bg-teal-50 px-3 py-1.5 text-sm dark:border-teal-900/40 dark:bg-teal-900/15">
          <span className="text-teal-700 dark:text-teal-300">Equivalentes (mismo principio activo): <span className="font-semibold">{pa}</span></span>
          <button className="ml-auto rounded px-2 py-0.5 text-xs text-teal-700 hover:bg-teal-100 dark:text-teal-300 dark:hover:bg-teal-900/30" onClick={() => setPa(null)}>× Quitar filtro</button>
        </div>
      )}
      <div className="mt-3 flex gap-3">
        <div className="max-h-[min(28rem,calc(100dvh-24rem))] min-w-0 flex-1 overflow-auto overscroll-contain">
          {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="text-left">
              <Th col="nombre">Producto</Th>
              <Th col="fracc" right>Fracc.</Th><Th col="marca">Marca</Th>
              <Th col="stock" right>Stock</Th><Th col="precio" right>P. s/IVA</Th>
              <Th col="desc" right>Desc.</Th><th className={`${th} text-center`}>IVA</th><Th col="final" right>P. final</Th>
            </tr></thead>
            <tbody>
              {vista.map((p, i) => {
                const precio = precioUnit(p);
                const pct = Number(p.descuento_pct) || 0;
                const descU = Math.round(precio * pct * 10000) / 1000000;
                const final = finalDe(p);
                const agotado = sinStock(p);
                return (
                  <tr key={p.id} ref={i === idx ? selRef : undefined} className={`scroll-mt-9 border-b last:border-0 ${agotado ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${navRowCls(i === idx)}`} onClick={() => pick(p)} onMouseEnter={() => setIdx(i)}>
                    <td className="py-1.5 px-2 max-w-[20rem]">
                      <div className="truncate" title={p.nombre}>
                        {p.nombre}
                        {p.clase_local && <span className={`ml-2 rounded px-1 py-0.5 text-[10px] font-bold ${p.clase_local === 'A' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : p.clase_local === 'B' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : p.clase_local === 'C' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`} title={`Clase local ${p.clase_local}${p.clase_empresa ? ` · empresa ${p.clase_empresa}` : ''}`}>{p.clase_local}</span>}
                        {p.es_lento && <span className="ml-1 text-[10px]" title="Lento movimiento (sobre-stock)">🐌</span>}
                        {agotado && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700 dark:bg-red-900/30 dark:text-red-300">Sin stock</span>}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground" title={[p.codigo, p.presentacion, p.principio_activo].filter(Boolean).join(' · ')}>
                        <span className="font-mono">{p.codigo}</span>{[p.presentacion, p.principio_activo].filter(Boolean).length > 0 ? ' · ' + [p.presentacion, p.principio_activo].filter(Boolean).join(' · ') : ''}
                      </div>
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{Number(p.fracciones) || 1}</td>
                    <td className="py-1.5 px-2 max-w-[10rem] truncate text-xs text-muted-foreground" title={p.marca ?? ''}>{p.marca ?? '—'}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums ${stockSemaforo(p).cls}`} title={stockTitle(p)}>
                      <span className={`mr-1 inline-block h-2 w-2 rounded-full align-middle ${stockSemaforo(p).cls.includes('red') ? 'bg-red-500' : stockSemaforo(p).cls.includes('orange') ? 'bg-orange-500' : stockSemaforo(p).cls.includes('amber') ? 'bg-amber-400' : stockSemaforo(p).cls.includes('emerald') ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                      {Number(p.stock).toLocaleString(LOCALE)}
                      {Number(p.transito_entrante ?? 0) > 0 && (
                        <span className="ml-1 whitespace-nowrap rounded bg-sky-100 px-1 text-[10px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
                          title={`Transferencia en camino: +${Number(p.transito_entrante)}${p.transito_eta ? ` · llega ~${p.transito_eta}` : ''}`}>
                          🚚+{Number(p.transito_entrante).toLocaleString(LOCALE)}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">
                      {money(precio, moneda)}{esFrac(p) && <span className="ml-1 text-[10px] text-muted-foreground">/u</span>}
                      {(Number(p.fracciones) || 1) > 1 && (
                        <div className="text-[10px] text-muted-foreground">
                          {esFrac(p) ? `caja ${money(precioCaja(p), moneda)}` : `u ${money(Math.round(precioCaja(p) / (Number(p.fracciones) || 1) * 100) / 100, moneda)}`}
                        </div>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">
                      {pct > 0 ? (
                        <div>
                          <span className="font-medium text-emerald-700 dark:text-emerald-400">{pct}%</span>
                          <span className="ml-1 text-xs text-muted-foreground">−{money(descU, moneda)}</span>
                          <div className={`text-[10px] uppercase ${p.descuento_origen === 'mayorista' ? 'font-semibold text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}`}>{ORIGEN_LABEL[p.descuento_origen] ?? ''}</div>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="py-1.5 px-2 text-center text-xs">{Number(p.iva_tarifa) > 0 ? `${Number(p.iva_tarifa)}%` : 'No'}</td>
                    <td className="py-1.5 px-2 text-right font-semibold tabular-nums">{money(final, moneda)}</td>
                  </tr>
                );
              })}
              {(catF || marcaF || pa || qd.trim().length >= 2) && !isFetching && vista.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">Sin resultados</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Vista previa del producto resaltado */}
        <aside className="hidden w-60 shrink-0 rounded-md border bg-muted/10 p-3 lg:block">
          {sel ? (
            <div className="space-y-2">
              <div className="flex h-48 items-center justify-center overflow-hidden rounded-md border bg-background">
                {(imagenSel ?? thumbs[sel.id])
                  ? <img src={imagenSel ?? thumbs[sel.id]} alt={sel.nombre} className="h-full w-full object-contain" />
                  : <div className="text-center text-muted-foreground/50"><ImageIcon className="mx-auto h-10 w-10" /><div className="mt-1 text-xs">Sin imagen</div></div>}
              </div>
              <div className="text-sm font-medium leading-tight">{sel.nombre}</div>
              <div className="font-mono text-xs text-muted-foreground">{sel.codigo}</div>
              {sel.presentacion && <div className="text-xs text-muted-foreground">{sel.presentacion}</div>}
              <div className="text-xs" title={stockTitle(sel)}>Stock: <span className={`font-semibold ${stockSemaforo(sel).cls}`}>{Number(sel.stock).toLocaleString(LOCALE)}</span>
                {Number(sel.stock_min) > 0 && <span className="text-muted-foreground"> · mín {Number(sel.stock_min).toLocaleString(LOCALE)} · máx {Number(sel.stock_max).toLocaleString(LOCALE)}</span>}</div>
              {Number(sel.transito_entrante ?? 0) > 0 && (
                <div className="rounded-md bg-sky-50 px-2 py-1 text-xs text-sky-800 dark:bg-sky-900/20 dark:text-sky-300">
                  🚚 En camino: <b>+{Number(sel.transito_entrante).toLocaleString(LOCALE)}</b>{sel.transito_eta ? ` · llega ~${new Date(`${sel.transito_eta}T00:00:00`).toLocaleDateString(LOCALE, { day: '2-digit', month: 'short' })}` : ''}
                  <div className="text-[10px] text-sky-700/80 dark:text-sky-400/80">Puedes ofrecer apartarlo — aún no es vendible</div>
                </div>
              )}
              {Number(sel.descuento_pct) > 0 && (
                <div className="rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
                  Descuento {Number(sel.descuento_pct)}% <span className="uppercase">({ORIGEN_LABEL[sel.descuento_origen] ?? ''})</span>
                </div>
              )}
              <div className="border-t pt-2">
                <div className="text-xs text-muted-foreground">Pagaría (con IVA y desc.)</div>
                <div className="text-2xl font-bold tabular-nums">{money(finalDe(sel), moneda)}</div>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-center text-xs text-muted-foreground/60">Resalta un producto<br />para ver su detalle</div>
          )}
        </aside>
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
        <span className="font-medium">Teclas:</span>{' '}
        <kbd className="rounded border px-1">↑</kbd>/<kbd className="rounded border px-1">↓</kbd> navegar ·{' '}
        <kbd className="rounded border px-1">Enter</kbd> agregar ·{' '}
        <kbd className="rounded border px-1">F2</kbd> limpiar búsqueda ·{' '}
        <kbd className="rounded border px-1">F4</kbd> stock por bodega ·{' '}
        <kbd className="rounded border px-1">F5</kbd> kardex ·{' '}
        <kbd className="rounded border px-1">F8</kbd> productos de la marca ·{' '}
        <kbd className="rounded border px-1">F9</kbd> equivalentes (mismo principio activo) ·{' '}
        <kbd className="rounded border px-1">Esc</kbd> cerrar
      </div>
      {detalle?.tipo === 'stock' && <StockSucursales producto={detalle.prod} onClose={() => setDetalle(null)} />}
      {detalle?.tipo === 'kardex' && <KardexProducto producto={detalle.prod} moneda={moneda} onClose={() => setDetalle(null)} />}
    </ModalBox>
  );
}

// ---- F4 (dentro de buscar): stock del producto en cada bodega ----
function StockSucursales({ producto, onClose }: { producto: ProductoBusqueda; onClose: () => void }) {
  const { data: filas = [], isFetching } = useQuery<StockSucursal[]>({ queryKey: ['pos-stock-suc', producto.id], queryFn: () => stockPorSucursal(producto.id), staleTime: 0 });
  const totCajas = filas.reduce((s, f) => s + Number(f.cajas), 0);
  const totFrac = filas.reduce((s, f) => s + Number(f.fracciones), 0);
  return (
    <ModalBox title={`Stock por bodega — ${producto.nombre}`} onClose={onClose} wide>
      <p className="mb-2 text-xs text-muted-foreground">Presentación: {producto.presentacion ?? '—'} · {Number(producto.fracciones) || 1} fracc./caja</p>
      <div className="max-h-96 overflow-auto">
        {isFetching && <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 px-2">Bodega</th><th className="py-2 px-2">Tipo</th>
            <th className="py-2 px-2 text-right">Cajas</th><th className="py-2 px-2 text-right">Fracciones</th>
          </tr></thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.location_id} className={`border-b last:border-0 ${Number(f.cajas) > 0 ? '' : 'text-muted-foreground'}`}>
                <td className="py-1.5 px-2">{f.codigo} · {f.nombre}</td>
                <td className="py-1.5 px-2 text-xs">{f.tipo}</td>
                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{Number(f.cajas).toLocaleString(LOCALE)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{Number(f.fracciones).toLocaleString(LOCALE)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t font-semibold">
            <td className="py-2 px-2" colSpan={2}>Total</td>
            <td className="py-2 px-2 text-right tabular-nums">{totCajas.toLocaleString(LOCALE)}</td>
            <td className="py-2 px-2 text-right tabular-nums">{totFrac.toLocaleString(LOCALE)}</td>
          </tr></tfoot>
        </table>
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground"><span className="font-medium">Teclas:</span> <kbd className="rounded border px-1">Esc</kbd> cerrar (vuelve al buscador)</div>
    </ModalBox>
  );
}

// ---- F2: historial de compras del cliente seleccionado (últimas 100 ventas) ----
function HistorialCliente({ cliente, moneda, onClose }: { cliente: Cliente; moneda: string; onClose: () => void }) {
  const { data: filas = [], isFetching } = useQuery<HistorialCompra[]>({
    queryKey: ['pos-historial-cliente', cliente.id], queryFn: () => historialCliente(cliente.id), staleTime: 0,
  });
  const [q, setQ] = useState('');
  const filtradas = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return filas;
    return filas.filter((f) => `${f.producto} ${f.documento ?? ''}`.toLowerCase().includes(s));
  }, [filas, q]);
  return (
    <ModalBox title={`Historial de compras — ${cliente.razon_social}`} onClose={onClose} xwide>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[16rem]">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input autoFocus className={`${fieldCls} h-9 pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por producto o documento…" />
        </div>
        <span className="text-xs text-muted-foreground">{filtradas.length} de {filas.length} · últimas 100 ventas{cliente.identificacion ? ` · ${cliente.identificacion}` : ''}</span>
      </div>
      <div className="max-h-[28rem] overflow-auto">
        {isFetching && <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 px-2">Fecha</th><th className="py-2 px-2">Tipo</th><th className="py-2 px-2">Documento</th>
            <th className="py-2 px-2">Producto</th><th className="py-2 px-2 text-right">Cantidad</th>
            <th className="py-2 px-2 text-right">Descuento</th><th className="py-2 px-2 text-right">Precio neto final</th>
          </tr></thead>
          <tbody>
            {filtradas.map((f, i) => (
              <tr key={i} className={`border-b last:border-0${f.precio_neto < 0 ? ' bg-amber-50/60 dark:bg-amber-900/10' : ''}`}>
                <td className="py-1.5 px-2 whitespace-nowrap text-xs">{new Date(f.fecha).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                <td className={`py-1.5 px-2 text-xs${f.tipo_documento === 'Nota de crédito' ? ' text-amber-700 dark:text-amber-400 font-medium' : ''}`}>{f.tipo_documento}</td>
                <td className="py-1.5 px-2 font-mono text-xs">{f.documento ?? '—'}</td>
                <td className="py-1.5 px-2 text-xs">{f.producto}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums${f.cantidad < 0 ? ' text-red-600 dark:text-red-400' : ''}`}>{Number(f.cantidad).toLocaleString(LOCALE)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-red-600 dark:text-red-400">{Number(f.descuento) > 0 ? money(f.descuento, moneda) : '—'}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums font-medium${f.precio_neto < 0 ? ' text-red-600 dark:text-red-400' : ''}`}>{money(f.precio_neto, moneda)}</td>
              </tr>
            ))}
            {!isFetching && filas.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Este cliente no tiene compras registradas</td></tr>}
            {!isFetching && filas.length > 0 && filtradas.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin resultados para «{q}»</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground"><kbd className="rounded border px-1">Esc</kbd> cerrar</div>
    </ModalBox>
  );
}

// ---- F5 (dentro de buscar): kardex del producto por rango de fechas ----
function KardexProducto({ producto, moneda, onClose }: { producto: ProductoBusqueda; moneda: string; onClose: () => void }) {
  const hoy = new Date();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [desde, setDesde] = useState(iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)));
  const [hasta, setHasta] = useState(iso(hoy));
  const { data: movs = [], isFetching } = useQuery<KardexMov[]>({ queryKey: ['pos-kardex', producto.id, desde, hasta], queryFn: () => kardexProducto(producto.id, desde, hasta), staleTime: 0 });
  return (
    <ModalBox title={`Kardex — ${producto.nombre}`} onClose={onClose} xwide>
      <div className="mb-2 text-xs text-muted-foreground"><span className="font-mono">{producto.codigo}</span> · Presentación: {producto.presentacion || '—'} · Fracc.: {producto.fracciones ?? '—'}</div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="space-y-1"><span className="text-xs text-muted-foreground">Desde</span><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="space-y-1"><span className="text-xs text-muted-foreground">Hasta</span><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <span className="text-xs text-muted-foreground">Cantidades en {producto.unidad_medida || 'unidad'} (presentación)</span>
      </div>
      <div className="max-h-96 overflow-auto">
        {isFetching && <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 px-2">Fecha y hora</th><th className="py-2 px-2">Bodega</th><th className="py-2 px-2">Movimiento</th>
            <th className="py-2 px-2">Cliente / Proveedor / Bodega</th>
            <th className="py-2 px-2">Documento</th><th className="py-2 px-2 text-right">Entrada</th>
            <th className="py-2 px-2 text-right">Salida</th><th className="py-2 px-2 text-right">Saldo</th><th className="py-2 px-2 text-right">Costo unit.</th>
          </tr></thead>
          <tbody>
            {movs.map((m, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1.5 px-2 whitespace-nowrap text-xs">{new Date(m.fecha).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</td>
                <td className="py-1.5 px-2 text-xs">{m.bodega}</td>
                <td className="py-1.5 px-2 text-xs">{m.tipo}</td>
                <td className="py-1.5 px-2 text-xs">{m.contraparte ?? '—'}</td>
                <td className="py-1.5 px-2 font-mono text-xs">{m.documento ?? '—'}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{Number(m.entrada) > 0 ? Number(m.entrada).toLocaleString(LOCALE) : ''}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-red-600 dark:text-red-400">{Number(m.salida) > 0 ? Number(m.salida).toLocaleString(LOCALE) : ''}</td>
                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{Number(m.saldo).toLocaleString(LOCALE)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{money(m.costo_unitario, moneda)}</td>
              </tr>
            ))}
            {!isFetching && movs.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Sin movimientos en el rango</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground"><span className="font-medium">Teclas:</span> edita <span className="font-medium">Desde/Hasta</span> para reconsultar · <kbd className="rounded border px-1">Esc</kbd> cerrar (vuelve al buscador)</div>
    </ModalBox>
  );
}

// ---- F5: buscar cliente ----
// Exportado: el Salón (restaurant) reutiliza este mismo buscador en el cobro.
export function BuscarCliente({ onPick, onClose }: { onPick: (c: Cliente) => void; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [crear, setCrear] = useState(false);
  const { data: clientes = [] } = useQuery<ClienteOpt[]>({
    queryKey: ['pos-clientes'],
    queryFn: async () => { try { return await clientesList(); } catch { return clientesOffline(); } },
  });
  const filt = useMemo(() => { const s = q.trim().toLowerCase(); return s ? clientes.filter((c) => `${c.razon_social} ${c.identificacion ?? ''}`.toLowerCase().includes(s)) : clientes.slice(0, 50); }, [clientes, q]);
  // Navegación por teclado: ↑/↓ mueven la selección, Enter elige el resaltado
  const [idx, setIdx] = useState(0);
  const selRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => { setIdx((i) => Math.min(i, Math.max(0, filt.length - 1))); }, [filt.length]);
  useEffect(() => { selRef.current?.scrollIntoView({ block: 'nearest' }); }, [idx]);
  const pick = (c: ClienteOpt) => onPick({ id: c.id, razon_social: c.razon_social, identificacion: c.identificacion });
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, filt.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filt[idx]) pick(filt[idx]); }
    else if (e.key === 'Insert') { e.preventDefault(); setCrear(true); }
  };
  if (crear) return (
    <CrearCliente initial={q} onClose={() => setCrear(false)}
      onCreated={(c) => { void qc.invalidateQueries({ queryKey: ['pos-clientes'] }); onPick(c); }} />
  );
  return (
    <ModalBox title="Seleccionar cliente (F5)" onClose={onClose} wide>
      <input autoFocus className={fieldCls} placeholder="Buscar por nombre o identificación…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} />
      <div className="mt-3 max-h-80 overflow-auto">
        <table className="w-full text-sm">
          <tbody>
            {filt.map((c, i) => (
              <tr key={c.id} ref={i === idx ? selRef : undefined} className={`border-b last:border-0 cursor-pointer ${navRowCls(i === idx)}`} onClick={() => pick(c)} onMouseEnter={() => setIdx(i)}>
                <td className="py-2 pr-3 font-mono text-xs">{c.identificacion ?? '—'}</td><td className="py-2 pr-3">{c.razon_social}</td>
              </tr>
            ))}
            {filt.length === 0 && <tr><td colSpan={2} className="py-4 text-center text-muted-foreground">Sin resultados</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
        <span className="font-medium">Teclas:</span>{' '}
        <kbd className="rounded border px-1">↑</kbd>/<kbd className="rounded border px-1">↓</kbd> navegar ·{' '}
        <kbd className="rounded border px-1">Enter</kbd> elegir ·{' '}
        <kbd className="rounded border px-1">Insert</kbd> crear cliente ·{' '}
        <kbd className="rounded border px-1">Esc</kbd> cerrar
      </div>
    </ModalBox>
  );
}

// ---- Insert: alta rápida de cliente desde el POS ----
function CrearCliente({ initial, onCreated, onClose }: { initial: string; onCreated: (c: Cliente) => void; onClose: () => void }) {
  const soloDigitos = /^\d+$/.test(initial.trim());
  const [f, setF] = useState({
    tipo_identificacion: 'cedula',
    identificacion: soloDigitos ? initial.trim() : '',
    razon_social: soloDigitos ? '' : initial.trim(),
    direccion: '', telefono: '', email: '',
  });
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!f.razon_social.trim()) { toast.error('Ingrese los nombres o la razón social'); return; }
    if (!f.identificacion.trim()) { toast.error('Ingrese la identificación'); return; }
    setSaving(true);
    try {
      const r = await clienteCrear({ ...f });
      toast.success(r.existe ? 'El cliente ya existía — se seleccionó' : 'Cliente creado');
      onCreated({ id: r.id, razon_social: r.razon_social, identificacion: r.identificacion });
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <ModalBox title="Nuevo cliente (Insert)" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1">
            <label className="mb-1 block text-xs text-muted-foreground">Tipo de documento</label>
            <select className={fieldCls} value={f.tipo_identificacion} onChange={(e) => set({ tipo_identificacion: e.target.value })}>
              <option value="cedula">Cédula</option>
              <option value="ruc">RUC</option>
              <option value="pasaporte">Pasaporte</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs text-muted-foreground">Identificación</label>
            <input autoFocus className={fieldCls} value={f.identificacion} onChange={(e) => set({ identificacion: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void guardar(); }} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Nombres o razón social</label>
          <input className={fieldCls} value={f.razon_social} onChange={(e) => set({ razon_social: e.target.value })} />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Dirección</label>
          <input className={fieldCls} value={f.direccion} onChange={(e) => set({ direccion: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Teléfono</label>
            <input className={fieldCls} value={f.telefono} onChange={(e) => set({ telefono: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Correo electrónico</label>
            <input type="email" className={fieldCls} value={f.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <button className="rounded-md border px-3 py-1.5 text-sm" onClick={onClose}>Cancelar</button>
          <button className="rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={saving} onClick={() => void guardar()}>{saving ? 'Guardando…' : 'Crear y seleccionar'}</button>
        </div>
      </div>
    </ModalBox>
  );
}

// ---- Espacio: cobro multipago ----
interface TicketLinea { descripcion: string; cantidad: number; precio_unitario?: number; descuento?: number; subtotal: number }
interface TicketData { codigo: string; tipoDoc?: 'factura' | 'nota_venta' | 'habitacion'; claveAcceso?: string | null; serie?: string | null; total: number; subtotal: number; iva: number; recibido: number; vuelto: number; cliente: string; caja?: string | null; lineas: TicketLinea[]; pagos?: { forma: string; monto: number }[]; puntosGanados?: number; puntosSaldo?: number; cashbackGanado?: number; monederoSaldo?: number; sellos?: { campana: string; sellos_ganados: number; premios: number }[]; habitacion?: string | null }
function Cobro({ caja, cliente, lineas, total, moneda, tipoDoc, puntos, monedero, premios, onClose, onVendida }: { caja: Caja; cliente: Cliente; lineas: Linea[]; total: number; moneda: string; tipoDoc: 'factura' | 'nota_venta'; puntos: number; monedero: number; premios: SelloPremio[]; onClose: () => void; onVendida: (t: TicketData) => void }) {
  const offline = !conexion.online;
  const { data: formas = [] } = useQuery<FormaPago[]>({
    queryKey: ['pos-formas-local', caja.location_id ?? '', offline],
    queryFn: async () => {
      if (offline) return (await formasOffline(caja.location_id)).filter((f) => f.tipo === 'efectivo');
      return formasPagoLocal(caja.location_id);
    },
  });
  // Cargo a la habitación (módulo Hotel, 921): solo aparece si la empresa tiene
  // hotel configurado (config_get devuelve null si no) y hay red (el folio/huésped
  // se valida en el servidor). `catch` deja el módulo invisible si no está desplegado.
  const { data: hotelCfg } = useQuery<{ activo?: boolean } | null>({
    queryKey: ['pos-hotel-config'], queryFn: () => hotelRpc<{ activo?: boolean } | null>('config_get').catch(() => null), staleTime: 5 * 60 * 1000,
  });
  const hotelDisponible = !!hotelCfg && !offline;
  const [modoCobro, setModoCobro] = useState<'pago' | 'habitacion'>('pago');
  const [buscaHuesped, setBuscaHuesped] = useState('');
  const [huespedSel, setHuespedSel] = useState<HuespedEnCasa | null>(null);
  const { data: huespedes = [], isLoading: huespedesLoading } = useQuery<HuespedEnCasa[]>({
    queryKey: ['pos-hotel-huespedes-en-casa'], queryFn: () => hotelRpc<HuespedEnCasa[]>('huespedes_en_casa'),
    enabled: modoCobro === 'habitacion', staleTime: 0,
  });
  const huespedesFiltrados = useMemo(() => {
    const q = buscaHuesped.trim().toLowerCase();
    if (!q) return huespedes;
    return huespedes.filter((h) => h.habitacion.toLowerCase().includes(q) || h.huesped.toLowerCase().includes(q) || h.codigo.toLowerCase().includes(q));
  }, [huespedes, buscaHuesped]);
  // Pasarela de cobro con tarjeta (scripts 879-880): los modos disponibles los
  // decide la configuración de ESTE módulo, no una bandera global.
  const [pasarelaPagoKey, setPasarelaPagoKey] = useState<number | null>(null);
  // 902 · Con qué medio se abrió el cobro. Decide qué le pide la caja a la
  // pasarela y, por tanto, qué ve el cliente al escanear.
  const [pasarelaMedio, setPasarelaMedio] = useState<'tarjeta' | 'deuna'>('tarjeta');
  const { data: modos } = useQuery<PasarelaModos>({
    queryKey: ['pos-pasarela-modos', 'pos'], queryFn: () => pasarelaModos('pos'), staleTime: 5 * 60 * 1000,
  });
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [canje, setCanje] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Cupón del signage (411): el cliente dice el código de la pantalla; se valida como
  // preview y pos_registrar_venta lo re-valida y canjea atómicamente al confirmar.
  const [cupon, setCupon] = useState('');
  const [cuponInfo, setCuponInfo] = useState<{ ok: boolean; motivo?: string; cupon?: { codigo: string; nombre: string; tipo: 'pct' | 'monto'; valor: number; producto: string | null } } | null>(null);
  const validarCupon = async () => {
    if (!cupon.trim()) { setCuponInfo(null); return; }
    try { setCuponInfo(await posRpc('cupon_validar', { _codigo: cupon.trim(), _caja_id: caja.id })); }
    catch (e) { setCuponInfo({ ok: false, motivo: getErrorMessage(e) }); }
  };
  const montoRef = useRef<HTMLInputElement>(null);  // foco inicial: teclear un número = valor recibido
  const toggleCanje = (id: string) => setCanje((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  // Forma de pago por defecto = efectivo (si no existe, la primera activa).
  const efectivoId = useMemo(() => formas.find((f) => f.tipo === 'efectivo')?.id ?? formas[0]?.id ?? '', [formas]);
  useEffect(() => { if (formas.length && pagos.length === 0) setPagos([{ key: _k++, forma_pago_id: efectivoId, monto: total, referencia: '' }]); }, [formas, total, pagos.length, efectivoId]);
  // Al aparecer el primer pago, enfoca y selecciona el monto (teclear reemplaza el valor).
  useEffect(() => { if (pagos.length === 1) { montoRef.current?.focus(); montoRef.current?.select(); } }, [pagos.length]);
  const recibido = useMemo(() => pagos.reduce((s, p) => s + (Number(p.monto) || 0), 0), [pagos]);
  const vuelto = redondearMoneda(recibido - total, moneda);
  const stepMonto = moneda === 'COP' ? '1' : '0.01';  // COP no usa centavos
  const setPago = (key: number, patch: Partial<Pago>) => setPagos((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const esTarjeta = (p: Pago) => formas.find((f) => f.id === p.forma_pago_id)?.tipo === 'tarjeta';
  // Recargo por tarjeta (solo DISPLAY — el cálculo autoritativo lo hace pos_registrar_venta):
  // por cada pago cuya forma tenga comision_pct > 0, recargo = round(monto * pct/100, 2).
  // El `monto` que se envía a la RPC NO cambia (sigue siendo la base que asigna el cajero).
  const recargoDe = (p: Pago): number => {
    const pct = Number(formas.find((f) => f.id === p.forma_pago_id)?.comision_pct) || 0;
    return pct > 0 ? redondearMoneda(((Number(p.monto) || 0) * pct) / 100, moneda) : 0;
  };
  const recargoTotal = pagos.reduce((s, p) => s + recargoDe(p), 0);
  // Guard "solo efectivo": el carrito trae algún producto solo_efectivo (recargas, etc.)
  // y el cajero eligió/tiene un pago con forma distinta de efectivo → bloquear confirmar.
  // El backend también lo valida; esto solo avisa antes de intentar cobrar.
  const haySoloEfectivo = lineas.some((l) => l.solo_efectivo);
  const pagoNoEfectivo = pagos.some((p) => formas.find((f) => f.id === p.forma_pago_id)?.tipo !== 'efectivo');
  const bloqueoSoloEfectivo = haySoloEfectivo && pagoNoEfectivo;
  const AVISO_SOLO_EFECTIVO = 'Hay productos que solo se cobran en efectivo (ej.: recargas). Cóbralos en efectivo o sepáralos en otra venta.';
  // Solo se envían cuando el pago es de tipo tarjeta (para el resto, ni se mandan).
  const datosTarjeta = (p: Pago) => (!esTarjeta(p) ? {} : {
    adquirente: p.adquirente || null,
    tarjeta_lote: p.tarjeta_lote || null,
    tarjeta_voucher: p.tarjeta_voucher || null,
    tarjeta_autorizacion: p.tarjeta_autorizacion || null,
    tarjeta_ultimos4: p.tarjeta_ultimos4 || null,
    tarjeta_marca: p.tarjeta_marca || null,
    tarjeta_terminal: p.tarjeta_terminal || null,
    tarjeta_diferido_meses: p.tarjeta_diferido_meses ? Number(p.tarjeta_diferido_meses) || null : null,
  });

  // Una vez que la tarjeta YA fue cobrada por la pasarela, salir de esta
  // pantalla sin confirmar deja el cobro huérfano: el cliente pagó y no hay
  // factura que lo respalde. Se cierran las salidas fáciles (Escape, clic
  // fuera, la X, Cancelar) y se avisa si intentan cerrar el navegador.
  const hayCobroPasarela = pagos.some((p) => p.pasarela_ok);
  const AVISO_NO_SALIR =
    'La tarjeta YA fue cobrada. Confirma la venta para que el cobro quede unido a la factura — '
    + 'si sales ahora, el cobro queda huérfano.';
  const intentarCerrar = () => {
    if (hayCobroPasarela) { toast.error(AVISO_NO_SALIR, { duration: 9000 }); return; }
    onClose();
  };
  useEffect(() => {
    if (!hayCobroPasarela) return;
    const avisar = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [hayCobroPasarela]);

  const confirmar = async () => {
    if (saving) return;
    if (bloqueoSoloEfectivo) { toast.error(AVISO_SOLO_EFECTIVO); return; }
    if (recibido + 0.0001 < total) { toast.error('El pago es menor al total'); return; }
    const tarjetasIncompletas = pagos.some((p) => esTarjeta(p) && !(p.tarjeta_lote && p.tarjeta_voucher && p.tarjeta_autorizacion));
    if (tarjetasIncompletas) toast.info('Pago con tarjeta sin lote/voucher/autorización — podrás completarlos luego, pero facilitan la conciliación con el banco');
    setSaving(true);
    try {
      // ================= VENTA OFFLINE (script 303) =================
      if (!conexion.online) {
        const clientUuid = uuid();
        let off: { secuencial: number; clave: string; serie: string } | null = null;
        if (tipoDoc === 'factura') {
          off = await claveOffline(caja.id);
          if (!off) { toast.error('Sin claves de acceso en reserva — vende como NOTA DE VENTA (F8) o espera la reconexión'); setSaving(false); return; }
        }
        const fechaVenta = new Date();
        const payloadOff = {
          caja_id: caja.id, cliente_partner_id: cliente.id, tipo_doc: tipoDoc, observaciones: 'Venta OFFLINE',
          client_uuid: clientUuid, fecha: fechaVenta.toISOString(),
          ...(off ? { offline: { secuencial: off.secuencial, clave_acceso: off.clave } } : {}),
          lineas: lineas.map((l) => ({ producto_id: l.producto_id, codigo: l.codigo, descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento: l.descuento, iva_tarifa: l.iva_tarifa })),
          pagos: pagos.map((p) => ({ forma_pago_id: p.forma_pago_id, monto: Number(p.monto) || 0, referencia: null })),
        };
        await encolarVenta({
          client_uuid: clientUuid, fecha: fechaVenta.toISOString(), caja_id: caja.id, caja_nombre: caja.nombre,
          total, tipo_doc: tipoDoc, serie: off?.serie ?? null, payload: payloadOff,
        });
        if (caja.location_id) void descontarStockLocal(caja.location_id, lineas.map((l) => ({ producto_id: l.producto_id, cantidad: l.cantidad })));
        toast.success(`Venta OFFLINE guardada · vuelto ${money(vuelto, moneda)} — se sincroniza al volver la red`);
        onVendida({
          codigo: off?.serie ?? 'NV (por sincronizar)', tipoDoc, claveAcceso: off?.clave ?? null, serie: off?.serie ?? null,
          total, subtotal: lineas.reduce((a, l) => a + l.cantidad * (l.precio_unitario - l.descuento), 0),
          iva: tipoDoc === 'nota_venta' ? 0 : total - lineas.reduce((a, l) => a + l.cantidad * (l.precio_unitario - l.descuento), 0),
          recibido, vuelto, cliente: cliente.razon_social, caja: caja.nombre,
          lineas: lineas.map((l) => ({ descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento: l.descuento, subtotal: l.cantidad * (l.precio_unitario - l.descuento) })),
          pagos: pagos.map((p) => ({ forma: formas.find((f) => f.id === p.forma_pago_id)?.nombre ?? 'Efectivo', monto: Number(p.monto) || 0 })),
        });
        setSaving(false);
        return;
      }
      // ================= VENTA ONLINE (flujo normal) =================
      const payload = {
        caja_id: caja.id, cliente_partner_id: cliente.id, tipo_doc: tipoDoc, observaciones: null,
        lineas: lineas.map((l) => ({ producto_id: l.producto_id, codigo: l.codigo, descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento: l.descuento, iva_tarifa: l.iva_tarifa })),
        pagos: pagos.map((p) => ({ forma_pago_id: p.forma_pago_id, monto: Number(p.monto) || 0, referencia: p.referencia || null, ...datosTarjeta(p) })),
        premios_canje: canje,
        ...(cupon.trim() && tipoDoc === 'factura' ? { cupon: cupon.trim() } : {}),
      };
      const r = await posRpc<{ codigo: string; subtotal: number; iva: number; total: number; vuelto: number; lineas: TicketLinea[]; clave_acceso?: string | null; serie?: string | null; cupon?: { codigo: string; nombre: string; descuento: number } | null; puntos_ganados?: number; puntos_saldo?: number; cashback_ganado?: number; monedero_saldo?: number; sellos?: { campana: string; sellos_ganados: number; premios: number }[] }>('pos_registrar_venta', { payload });
      toast.success(`Venta ${r.codigo} · vuelto ${money(r.vuelto, moneda)}`);
      if (r.cupon) toast.success(`Cupón ${r.cupon.codigo} canjeado · descuento ${money(r.cupon.descuento, moneda)}`);
      // pantalla de cliente: "gracias + vuelto"; al vaciarse el carrito vuelve a la playlist
      pushCaja(caja.location_id, caja.id, [], { estado: 'pagado', total: r.total, vuelto: r.vuelto });
      const pagosTicket = pagos.map((p) => ({ forma: formas.find((f) => f.id === p.forma_pago_id)?.nombre ?? 'Pago', monto: Number(p.monto) || 0 }));
      onVendida({ codigo: r.codigo, tipoDoc, claveAcceso: r.clave_acceso, serie: r.serie, total: r.total, subtotal: r.subtotal, iva: r.iva, recibido, vuelto: r.vuelto, cliente: cliente.razon_social, caja: caja.nombre, lineas: r.lineas, pagos: pagosTicket, puntosGanados: r.puntos_ganados, puntosSaldo: r.puntos_saldo, cashbackGanado: r.cashback_ganado, monederoSaldo: r.monedero_saldo, sellos: r.sellos });
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };

  // Cargo a la habitación (módulo Hotel, 921): sin pagos — el consumo va al folio
  // del huésped y se cobra en el check-out. `cliente_partner_id` debe ser el
  // TITULAR de la reserva (no el cliente elegido en el POS, normalmente Consumidor Final).
  const confirmarHabitacion = async () => {
    if (saving) return;
    if (!huespedSel) { toast.error('Seleccione la habitación / huésped'); return; }
    if (haySoloEfectivo) { toast.error('Hay productos que solo se cobran en efectivo (ej.: recargas) — no se pueden cargar a la habitación.'); return; }
    setSaving(true);
    try {
      const payload = {
        caja_id: caja.id, cliente_partner_id: huespedSel.titular_partner_id, tipo_doc: 'habitacion', observaciones: null,
        hotel_reserva_id: huespedSel.reserva_id,
        lineas: lineas.map((l) => ({ producto_id: l.producto_id, codigo: l.codigo, descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento: l.descuento, iva_tarifa: l.iva_tarifa })),
        pagos: [],
      };
      const r = await posRpc<{ codigo: string; subtotal: number; iva: number; total: number; lineas: TicketLinea[] }>('pos_registrar_venta', { payload });
      toast.success(`Cargo ${r.codigo} enviado a la habitación ${huespedSel.habitacion}`);
      pushCaja(caja.location_id, caja.id, [], { estado: 'pagado', total: r.total, vuelto: 0 });
      onVendida({
        codigo: r.codigo, tipoDoc: 'habitacion', claveAcceso: null, serie: null, total: r.total, subtotal: r.subtotal, iva: r.iva,
        recibido: r.total, vuelto: 0, cliente: huespedSel.huesped, caja: caja.nombre, lineas: r.lineas, pagos: [], habitacion: huespedSel.habitacion,
      });
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };

  const confirmarActivo = modoCobro === 'habitacion' ? confirmarHabitacion : confirmar;
  const disabledConfirm = modoCobro === 'habitacion' ? (!huespedSel || haySoloEfectivo) : (bloqueoSoloEfectivo || recibido + 0.0001 < total);

  // Enter = Confirmar venta (desde cualquier campo del modal). El ref evita closures viejos.
  const confirmarRef = useRef(confirmarActivo);
  confirmarRef.current = confirmarActivo;
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void confirmarRef.current(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <ModalBox title={`Cobrar · ${money(total, moneda)}`} onClose={intentarCerrar}>
      <div className="space-y-3">
        {hotelDisponible && (
          <div className="grid grid-cols-2 overflow-hidden rounded-md border text-sm">
            <button type="button" disabled={hayCobroPasarela} className={`py-1.5 disabled:opacity-50 ${modoCobro === 'pago' ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`} onClick={() => setModoCobro('pago')}>Cobro normal</button>
            <button type="button" disabled={hayCobroPasarela} title={hayCobroPasarela ? AVISO_NO_SALIR : undefined} className={`py-1.5 disabled:opacity-50 ${modoCobro === 'habitacion' ? 'bg-sky-600 text-white font-medium' : 'hover:bg-muted'}`} onClick={() => setModoCobro('habitacion')}>Cargar a la habitación</button>
          </div>
        )}
        {modoCobro === 'habitacion' && (
          <div className="space-y-2">
            <label className="block space-y-1"><span className="text-xs text-muted-foreground">Buscar por n.º de habitación o nombre del huésped</span>
              <input className={fieldCls} autoFocus placeholder="Ej. 204 o Pérez" value={buscaHuesped} onChange={(e) => setBuscaHuesped(e.target.value)} /></label>
            <div className="max-h-48 overflow-auto rounded-md border divide-y">
              {huespedesLoading && <div className="p-2 text-xs text-muted-foreground">Cargando huéspedes en casa…</div>}
              {!huespedesLoading && huespedesFiltrados.length === 0 && <div className="p-2 text-xs text-muted-foreground">No hay huéspedes en casa que coincidan</div>}
              {huespedesFiltrados.map((h) => (
                <button key={h.reserva_id} type="button" onClick={() => setHuespedSel(h)}
                  className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted ${huespedSel?.reserva_id === h.reserva_id ? 'bg-primary/10' : ''}`}>
                  <span><b>Hab. {h.habitacion}</b> · {h.huesped} <span className="text-xs text-muted-foreground">({h.codigo})</span></span>
                  <span className="text-right text-xs text-muted-foreground">Saldo {money(h.saldo, moneda)}{h.limite_credito != null ? ` / límite ${money(h.limite_credito, moneda)}` : ''}</span>
                </button>
              ))}
            </div>
            {huespedSel && (
              <div className="rounded-md border p-2 text-xs space-y-1">
                <Row k="Saldo actual del folio" v={money(huespedSel.saldo, moneda)} />
                <div className="flex justify-between font-medium"><span>Saldo tras este cargo</span><span>{money(huespedSel.saldo + total, moneda)}</span></div>
                {huespedSel.limite_credito != null && huespedSel.saldo + total > huespedSel.limite_credito && (
                  <p className="text-red-600 dark:text-red-400">⚠ Supera el límite de crédito del huésped ({money(huespedSel.limite_credito, moneda)}). Puede continuar, pero avise a recepción.</p>
                )}
              </div>
            )}
            {haySoloEfectivo && (
              <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-300">
                ⚠ Hay productos que solo se cobran en efectivo (ej.: recargas) — no se pueden cargar a la habitación.
              </div>
            )}
          </div>
        )}
        {modoCobro === 'pago' && pagos.map((p, i) => (
          <div key={p.key} className="space-y-1.5">
            <div className="grid grid-cols-[1fr_7rem_2rem] gap-2 items-end">
              <label className="block space-y-1"><span className="text-xs text-muted-foreground">Forma de pago</span>
                <select className={fieldCls} value={p.forma_pago_id} onChange={(e) => setPago(p.key, { forma_pago_id: e.target.value, referencia: '' })}>
                  {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                </select></label>
              <label className="block space-y-1"><span className="text-xs text-muted-foreground">Monto recibido</span>
                <input ref={i === 0 ? montoRef : undefined} autoFocus={i === 0} onFocus={(e) => e.currentTarget.select()}
                  type="number" step={stepMonto} className={`${fieldCls} text-right`} value={p.monto} onChange={(e) => setPago(p.key, { monto: Number(e.target.value) || 0 })} /></label>
              <button className="h-10 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setPagos((ps) => ps.filter((x) => x.key !== p.key))}><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
            {modos?.enlace && modos.forma_pago_id === p.forma_pago_id && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={(Number(p.monto) || 0) <= 0}
                        onClick={() => { setPasarelaMedio('tarjeta'); setPasarelaPagoKey(p.key); }}>
                  <QrCode className="w-4 h-4 mr-1" /> Cobrar con tarjeta (enlace)
                </Button>
                {p.referencia && <span className="text-xs text-emerald-700 dark:text-emerald-400">✓ Pagado · ref {p.referencia}</span>}
              </div>
            )}
            {/* 902 · De Una va por la MISMA pasarela, pero con su propia forma
                de pago: es código SRI 20 y la tarjeta 19. */}
            {modos?.deuna && modos.forma_pago_deuna_id === p.forma_pago_id && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={(Number(p.monto) || 0) <= 0}
                        onClick={() => { setPasarelaMedio('deuna'); setPasarelaPagoKey(p.key); }}>
                  <QrCode className="w-4 h-4 mr-1" /> Cobrar con De Una (QR)
                </Button>
                {p.referencia && <span className="text-xs text-emerald-700 dark:text-emerald-400">✓ Pagado · ref {p.referencia}</span>}
              </div>
            )}
            {formas.find((f) => f.id === p.forma_pago_id)?.requiere_referencia
              && !(modos?.enlace && modos.forma_pago_id === p.forma_pago_id)
              && !(modos?.deuna && modos.forma_pago_deuna_id === p.forma_pago_id) && (
              <input className={`${fieldCls} h-9`} placeholder="Referencia / N° de comprobante" value={p.referencia}
                onChange={(e) => setPago(p.key, { referencia: e.target.value })} />
            )}
            {esTarjeta(p) && (
              <div className="rounded-md border border-dashed p-2 space-y-1.5 bg-muted/20">
                <p className="text-xs font-medium text-muted-foreground">
                  Datos de la tarjeta (conciliación con el banco)
                  {p.pasarela_ok && <span className="ml-2 font-normal text-emerald-700 dark:text-emerald-400">· los llenó la pasarela</span>}
                </p>
                {p.pasarela_ok ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {([
                      ['Adquirente', p.adquirente],
                      ['Marca', p.tarjeta_marca],
                      ['Autorización', p.tarjeta_autorizacion],
                      ['Lote', p.tarjeta_lote],
                      ['Terminal', p.tarjeta_terminal],
                      ['Últimos 4', p.tarjeta_ultimos4 ? `····${p.tarjeta_ultimos4}` : ''],
                    ] as [string, string | undefined][]).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-mono">{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Adquirente</span>
                    <select className={`${fieldCls} h-9`} value={p.adquirente ?? ''} onChange={(e) => setPago(p.key, { adquirente: e.target.value })}>
                      <option value="">—</option>
                      <option value="datafast">Datafast</option>
                      <option value="medianet">Medianet</option>
                      <option value="otro">Otro</option>
                    </select></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Marca</span>
                    <select className={`${fieldCls} h-9`} value={p.tarjeta_marca ?? ''} onChange={(e) => setPago(p.key, { tarjeta_marca: e.target.value })}>
                      <option value="">—</option>
                      <option value="Visa">Visa</option>
                      <option value="Mastercard">Mastercard</option>
                      <option value="Diners">Diners</option>
                      <option value="American Express">American Express</option>
                      <option value="Otra">Otra</option>
                    </select></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">N.º de lote *</span>
                    <input className={`${fieldCls} h-9`} value={p.tarjeta_lote ?? ''} onChange={(e) => setPago(p.key, { tarjeta_lote: e.target.value })} /></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">N.º de voucher / recibo *</span>
                    <input className={`${fieldCls} h-9`} value={p.tarjeta_voucher ?? ''} onChange={(e) => setPago(p.key, { tarjeta_voucher: e.target.value })} /></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Código de autorización *</span>
                    <input className={`${fieldCls} h-9`} value={p.tarjeta_autorizacion ?? ''} onChange={(e) => setPago(p.key, { tarjeta_autorizacion: e.target.value })} /></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Últimos 4 dígitos</span>
                    <input className={`${fieldCls} h-9`} inputMode="numeric" maxLength={4} value={p.tarjeta_ultimos4 ?? ''}
                      onChange={(e) => setPago(p.key, { tarjeta_ultimos4: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">N.º de terminal</span>
                    <input className={`${fieldCls} h-9`} value={p.tarjeta_terminal ?? ''} onChange={(e) => setPago(p.key, { tarjeta_terminal: e.target.value })} /></label>
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Diferido (meses)</span>
                    <input className={`${fieldCls} h-9`} type="number" min="0" step="1" value={p.tarjeta_diferido_meses ?? ''}
                      onChange={(e) => setPago(p.key, { tarjeta_diferido_meses: e.target.value })} /></label>
                </div>
                )}
                {!p.pasarela_ok && (
                  <p className="text-[11px] text-muted-foreground">* recomendados para cruzar con el archivo de Datafast/Medianet — no bloquean el cobro.</p>
                )}
              </div>
            )}
          </div>
        ))}
        {modoCobro === 'pago' && (
        <button className="text-xs text-primary hover:underline" onClick={() => setPagos((ps) => [...ps, { key: _k++, forma_pago_id: efectivoId, monto: Math.max(0, total - recibido), referencia: '' }])}>+ agregar forma de pago</button>
        )}
        {modoCobro === 'pago' && tipoDoc === 'factura' && !offline && (
          <div className="space-y-1">
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <label className="block space-y-1"><span className="text-xs text-muted-foreground">Cupón (código de la pantalla, opcional)</span>
                <input className={`${fieldCls} font-mono uppercase`} placeholder="p. ej. AHORRA10" value={cupon}
                  onChange={(e) => { setCupon(e.target.value.toUpperCase()); setCuponInfo(null); }}
                  onBlur={() => void validarCupon()} /></label>
              <Button size="sm" variant="outline" className="h-10" disabled={!cupon.trim()} onClick={() => void validarCupon()}>Validar</Button>
            </div>
            {cuponInfo && (cuponInfo.ok
              ? <p className="text-xs text-emerald-700 dark:text-emerald-400">✓ {cuponInfo.cupon!.nombre} — {cuponInfo.cupon!.tipo === 'pct' ? `${cuponInfo.cupon!.valor}%` : money(Number(cuponInfo.cupon!.valor), moneda)}{cuponInfo.cupon!.producto ? ` en ${cuponInfo.cupon!.producto}` : ' de descuento'} · se aplica al confirmar</p>
              : <p className="text-xs text-red-600 dark:text-red-400">✗ {cuponInfo.motivo}</p>)}
          </div>
        )}
        {modoCobro === 'pago' && bloqueoSoloEfectivo && (
          <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-300">
            ⚠ {AVISO_SOLO_EFECTIVO}
          </div>
        )}
        {modoCobro === 'pago' && puntos > 0 && <p className="text-xs text-amber-700 dark:text-amber-300">Saldo de puntos del cliente: {puntos.toLocaleString(LOCALE)} — canjea eligiendo la forma «Puntos» e ingresando el monto en $.</p>}
        {modoCobro === 'pago' && monedero > 0 && <p className="text-xs text-emerald-700 dark:text-emerald-300">Saldo de monedero del cliente: {money(monedero, moneda)} — úsalo eligiendo la forma «Monedero» e ingresando el monto.</p>}
        {modoCobro === 'pago' && premios.length > 0 && (
          <div className="rounded-md border border-rose-200 bg-rose-50/60 p-2 dark:border-rose-900/40 dark:bg-rose-900/10">
            <p className="mb-1 text-xs font-medium text-rose-700 dark:text-rose-300">🎁 Premios de sellos disponibles</p>
            {premios.map((pr) => (
              <label key={pr.id} className="flex items-center gap-2 py-0.5 text-xs">
                <input type="checkbox" checked={canje.includes(pr.id)} onChange={() => toggleCanje(pr.id)} />
                <span>{pr.descripcion}{pr.campana ? ` · ${pr.campana}` : ''}</span>
              </label>
            ))}
          </div>
        )}
        <div className="border-t pt-2 space-y-1">
          <Row k="Total" v={money(total, moneda)} />
          {modoCobro === 'pago' && <Row k="Recibido" v={money(recibido, moneda)} />}
          {modoCobro === 'pago' && recargoTotal > 0 && (
            <>
              <Row k="Recargo por tarjeta" v={money(recargoTotal, moneda)} />
              <div className="flex justify-between font-semibold text-amber-700 dark:text-amber-400">
                <span>Total con recargo</span><span>{money(total + recargoTotal, moneda)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">El monto que se cargará a la tarjeta incluye el recargo; el sistema lo suma automáticamente al confirmar.</p>
            </>
          )}
          {modoCobro === 'pago' && <div className={`flex justify-between font-semibold ${vuelto < 0 ? 'text-red-600' : 'text-emerald-600'}`}><span>Vuelto</span><span>{money(vuelto, moneda)}</span></div>}
          {modoCobro === 'habitacion' && (
            <div className="flex justify-between font-semibold text-sky-700 dark:text-sky-400">
              <span>Se carga al folio{huespedSel ? ` · Hab. ${huespedSel.habitacion}` : ''}</span><span>{money(total, moneda)}</span>
            </div>
          )}
        </div>
        {hayCobroPasarela && (
          <div className="rounded-md border border-urgente/40 bg-urgente-soft p-2 text-xs text-urgente">
            La tarjeta ya fue cobrada. <strong>Confirma la venta</strong> para unir el cobro a la factura.
          </div>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={intentarCerrar} disabled={hayCobroPasarela}
                  title={hayCobroPasarela ? AVISO_NO_SALIR : undefined}>Cancelar</Button>
          <Button isLoading={saving} disabled={disabledConfirm} onClick={confirmarActivo}>
            {modoCobro === 'habitacion' ? 'Cargar a la habitación (Enter)' : 'Confirmar venta (Enter)'}
          </Button>
        </div>
      </div>
      {pasarelaPagoKey !== null && (
        <CobroPasarela
          // A la tarjeta se le cobra CON el recargo por comisión: es lo que el
          // cliente termina debiendo (pos_registrar_venta lo añade al total).
          // Mandar la base dejaba el recargo sin cobrar — medido: cobro de 1,15
          // contra una venta de 1,24, nueve centavos que nadie pagó.
          monto={(() => {
            const pg = pagos.find((x) => x.key === pasarelaPagoKey);
            return pg ? (Number(pg.monto) || 0) + recargoDe(pg) : 0;
          })()}
          modulo="pos"
          cajaId={caja.id}
          partnerId={cliente?.id ?? null}
          moneda={moneda}
          medio={pasarelaMedio}
          onQrListo={(img) => pushCaja(caja.location_id, caja.id, [], { estado: 'qr', qr: img, total: Number(pagos.find((x) => x.key === pasarelaPagoKey)?.monto) || 0 })}
          onPagado={(ref, extra) => {
            // Se llenan los MISMOS campos que el cajero teclearía tras pasar la
            // tarjeta por el datáfono. Así la conciliación bancaria (script 882)
            // casa esta venta sin cambiar nada de su lógica, y sin que nadie
            // copie a mano un código de autorización.
            // Con De Una no hay datos de tarjeta que guardar: el proveedor no
            // los envía y rellenarlos inventaría una conciliación que no existe.
            if (pasarelaMedio === 'deuna') {
              setPago(pasarelaPagoKey, { referencia: ref, pasarela_ok: true });
              setPasarelaPagoKey(null);
              return;
            }
            setPago(pasarelaPagoKey, {
              referencia: ref,
              adquirente: extra.adquirente,
              tarjeta_autorizacion: extra.autorizacion ?? '',
              tarjeta_ultimos4: extra.ultimos4 ?? '',
              tarjeta_marca: extra.marca ?? '',
              tarjeta_lote: extra.lote ?? '',
              tarjeta_terminal: extra.terminal ?? '',
              // El comprobante de la pasarela es su propia referencia: no hay
              // voucher de papel que transcribir.
              tarjeta_voucher: ref,
              pasarela_ok: true,
            });
            setPasarelaPagoKey(null);
          }}
          onClose={() => setPasarelaPagoKey(null)}
        />
      )}
    </ModalBox>
  );
}


// ---- F10: suspendidas ----
function Suspendidas({ caja, cliente, lineas, total, moneda, onSuspendida, onRetomar, onClose }: {
  caja: Caja; cliente: Cliente | null; lineas: Linea[]; total: number; moneda: string;
  onSuspendida: () => void; onRetomar: (c: { cliente: Cliente; lineas: Linea[] }) => void; onClose: () => void;
}) {
  const { data: lista = [], refetch } = useQuery<{ id: string; etiqueta: string | null; total: number; items: number }[]>({ queryKey: ['pos-susp', caja.id], queryFn: () => posRpc('pos_suspendidas_listar', { p_caja_id: caja.id }), staleTime: 0, refetchOnMount: 'always' });

  const suspender = async () => {
    if (!lineas.length) { toast.error('No hay nada que suspender'); return; }
    try {
      await posRpc('pos_suspender_venta', { payload: { caja_id: caja.id, cliente_partner_id: cliente?.id ?? null, etiqueta: cliente?.razon_social ?? null, total: Math.round(total * 100) / 100, items: lineas.length, carrito: { cliente, lineas } } });
      toast.success('Venta suspendida'); onSuspendida();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const retomar = async (id: string) => {
    try {
      const c = await posRpc<{ cliente: Cliente; lineas: Linea[] } | null>('pos_suspendida_obtener', { p_id: id });
      if (!c || !Array.isArray(c.lineas)) { toast.error('Esa transacción ya no está disponible; se actualizó la lista'); void refetch(); return; }
      // "Cambiar": si el carrito actual tiene líneas, suspéndelo primero para no perderlo
      if (lineas.length) {
        await posRpc('pos_suspender_venta', { payload: { caja_id: caja.id, cliente_partner_id: cliente?.id ?? null, etiqueta: cliente?.razon_social ?? null, total: Math.round(total * 100) / 100, items: lineas.length, carrito: { cliente, lineas } } });
      }
      await posRpc('pos_suspendida_eliminar', { p_id: id });
      onRetomar(c);
      toast.success(lineas.length ? 'Cuenta cambiada (la anterior quedó suspendida)' : 'Transacción retomada');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const borrar = async (id: string) => { try { await posRpc('pos_suspendida_eliminar', { p_id: id }); refetch(); } catch (e) { toast.error(getErrorMessage(e)); } };

  return (
    <ModalBox title="Transacciones suspendidas (F10)" onClose={onClose}>
      <Button variant="outline" className="w-full mb-3" disabled={!lineas.length} onClick={suspender}>Suspender la venta actual ({money(total, moneda)})</Button>
      <div className="max-h-72 overflow-auto">
        {lista.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No hay ventas suspendidas</p>}
        {lista.map((s) => (
          <div key={s.id} className="flex items-center justify-between border-b py-2 last:border-0">
            <div><div className="text-sm font-medium">{s.etiqueta || 'Sin cliente'}</div><div className="text-xs text-muted-foreground">{s.items} ítem(s) · {money(s.total, moneda)}</div></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => retomar(s.id)}>Retomar</Button>
              <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => borrar(s.id)}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </ModalBox>
  );
}

// Respuesta de pos_registrar_venta cuando se factura la recarga (Fase 5).
interface FacturaRecargaResp {
  codigo: string; subtotal: number; iva: number; total: number; vuelto?: number;
  clave_acceso?: string | null; serie?: string | null; lineas: TicketLinea[];
}

// ---- F4: venta de recargas Broadnet (solo EFECTIVO; transacción independiente
// de la factura — Broadnet emite su propia nota de venta, salvo que la config
// diga "el comercio factura": ahí se emite una FACTURA SRI en vez del voucher) ----
function RecargaBroadnet({ caja, cliente, moneda, emp, cajero, onClose }: { caja: Caja; cliente: Cliente | null; moneda: string; emp: EmpresaTicket; cajero?: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: productos = [] } = useQuery<BnProductoFull[]>({ queryKey: ['pos-bn-productos'], queryFn: bnProductosActivos, staleTime: 5 * 60 * 1000 });
  const { data: pendientes = [] } = useQuery<{ id: string; estado: string }[]>({
    queryKey: ['pos-bn-pendientes', caja.id], queryFn: () => bnPendientesCaja(caja.id), staleTime: 0, refetchOnMount: 'always',
    // el worker resuelve en segundos: re-consultar mientras haya pendientes (702)
    refetchInterval: (q) => (((q.state.data as unknown[] | undefined)?.length ?? 0) > 0 ? 4000 : false),
  });
  // Config de facturación de recargas (Fase 5) — cache corta: puede cambiar en Conexión Broadnet.
  const { data: bnCfg } = useQuery<BnConfigFull>({ queryKey: ['pos-bn-config-recarga'], queryFn: bnConfigGet, staleTime: 60 * 1000 });
  // Opción B: se factura por operadora; el producto de inventario se asegura on-demand
  // (bn_recarga_producto_asegurar) según el broadnet_producto vendido, no un genérico.
  // El ajuste de la empresa sólo gobierna mientras no haya operadora elegida
  // (es lo que decide si se cargan las formas de pago). En cuanto se escoge,
  // manda lo que diga ESE producto — ver más abajo `facturaComercio`.
  const facturaComercioEmpresa = bnCfg?.recarga_facturacion === 'comercio';
  const { data: formasLocal = [] } = useQuery<FormaPago[]>({
    queryKey: ['pos-formas-local-recarga', caja.location_id ?? ''], queryFn: () => formasPagoLocal(caja.location_id),
    // Se cargan siempre: una operadora concreta puede facturar aunque la
    // empresa esté en «recibo», y al elegirla ya no da tiempo a pedirlas.
    staleTime: 5 * 60 * 1000,
  });
  const efectivoFormaId = useMemo(() => formasLocal.find((f) => f.tipo === 'efectivo')?.id ?? null, [formasLocal]);

  // Pestañas del modal F4: recargas móviles/TV (flujo propio) o pago de servicios
  // básicos (reutiliza ServiciosBasicos de Recargas.tsx, que es autocontenido).
  const [tabBn, setTabBn] = useState<'recargas' | 'servicios'>('recargas');
  const [productoId, setProductoId] = useState('');
  const [monto, setMonto] = useState('');
  const [celular, setCelular] = useState('');
  const [celular2, setCelular2] = useState('');
  const [montoLibre, setMontoLibre] = useState(false);   // 911: eligió «Otro monto»
  // 914: precio de la tarjeta = lo que llega al celular; `monto` guarda lo que se
  // pasa a bn_vender (tarjeta + comisión si la captura es «total»).
  const [montoTarjeta, setMontoTarjeta] = useState<number | null>(null);
  const [calculando, setCalculando] = useState(false);
  const elegirTarjeta = async (p: BnProductoFull, m: number | null) => {
    setProductoId(p.id); setCelular(''); setCelular2(''); setVentaError(null);
    setMontoLibre(m == null); setMontoTarjeta(m); setMonto('');
    if (m == null) return;
    setCalculando(true);
    try { setMonto(String((await bnTecleadoParaRecarga(p.id, m)).tecleado)); }
    catch (e) { setVentaError(getErrorMessage(e)); }
    finally { setCalculando(false); }
  };
  const [resultado, setResultado] = useState<BnVentaResp | null>(null);
  // Desglose EN VIVO de lo que teclea el cajero: qué se va a recargar y qué se
  // va a cobrar. Sin esto, en modo «total» el cajero no sabe cuánto llega al
  // celular hasta después de vender.
  const montoNum = Number(monto) || 0;
  const { data: desglose } = useQuery<BnDesglose>({
    queryKey: ['pos-bn-desglose', productoId, montoNum],
    queryFn: () => bnGestionDesglosar(productoId, montoNum),
    enabled: !!productoId && montoNum > 0,
    staleTime: 60 * 1000,
  });
  const [facturaOk, setFacturaOk] = useState<{ codigo: string; ticket: TicketImprimible } | null>(null);
  const [facturaError, setFacturaError] = useState<string | null>(null);
  // Caja de error PERSISTENTE de la venta (validación o lo que devuelva el WS de
  // Broadnet vía onError) — a diferencia del toast, no desaparece sola.
  const [ventaError, setVentaError] = useState<string | null>(null);

  const invalidar = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['pos-bn-pendientes', caja.id] });
    void qc.invalidateQueries({ queryKey: ['pos-bn-tx'] });
  }, [qc, caja.id]);

  const producto = productos.find((p) => p.id === productoId);
  // Quién factura ESTA recarga (814): lo dice el producto, con la herencia ya
  // resuelta por la base. Antes era una sola decisión para toda la empresa.
  const facturaComercio = producto
    ? producto.facturacion_efectiva === 'comercio'
    : facturaComercioEmpresa;
  const imprimirVoucher = (r: BnVentaResp) => imprimirVoucherRecarga({
    proveedor: r.proveedor, servicio: producto?.servicio_nombre ?? null, celular: r.celular,
    monto: Number(r.monto), autorizacion: r.autorizacion, notaVenta: r.nota_venta,
    referencia: r.referencia, caja: caja.nombre, cajero,
    // Con comisión de gestión el cliente pagó más que la recarga acreditada:
    // el voucher lo desglosa o el reclamo viene solo.
    gestion: Number(r.gestion) || 0,
    totalCobrado: Number(r.monto_cobrado) || undefined,
  }, emp);

  // Emite la FACTURA SRI de la recarga (línea única, precio_fijo=true → el server
  // NO recalcula por catálogo/promos, respeta precio_unitario/iva_tarifa tal cual).
  // El monto cobrado en efectivo es el valor de la recarga (IVA INCLUIDO, no adicional).
  // IVA RESIDUAL (script 658): base = round(monto/(1+iva/100)); iva_monto = monto − base.
  // Se manda iva_monto en la línea y pos_registrar_venta lo usa EXACTO ⇒ base + iva = el
  // monto al centavo, en TODOS los importes (p.ej. $10 → 8.70 + 1.30), sin vuelto de 0.01.
  // Best-effort: si falla, la recarga YA quedó acreditada — NO se revierte; se avisa
  // para reintentar manualmente (botón "Reintentar factura").
  const facturar = useMutation({
    mutationFn: async (r: BnVentaResp) => {
      if (!cliente) throw new Error('Sin cliente resuelto (Consumidor Final) — no se puede facturar');
      if (!efectivoFormaId) throw new Error('El local no tiene una forma de pago EFECTIVO habilitada');
      if (!productoId) throw new Error('No se pudo identificar la operadora de la recarga');
      // Producto de inventario de ESA operadora (REC-<cod>) — se crea/asegura si falta.
      const prodInvId = await bnRecargaProductoAsegurar(productoId);
      const gestionProdId = (Number(r.gestion) || 0) > 0 ? await bnGestionProductoAsegurar() : null;
      const ivaPct = Number(bnCfg?.recarga_iva_tarifa) || 0;
      // `r.monto` es SÓLO la recarga. La factura va por lo que pagó el cliente,
      // y la comisión de gestión sale como su propia línea (832): el cliente
      // tiene derecho a ver por qué pagó 1,10 y no 1,05.
      const montoTotal = Number(r.monto) || 0;
      const gestion = Number(r.gestion) || 0;
      const gestionIva = Number(r.gestion_iva) || 0;
      const gestionBase = Math.round((gestion - gestionIva) * 100) / 100;
      const gestionIvaPct = gestionBase > 0 && gestionIva > 0
        ? Math.round((gestionIva / gestionBase) * 10000) / 100 : 0;
      const cobrado = Number(r.monto_cobrado) || montoTotal + gestion;
      // IVA residual: la base es el monto sin IVA; el iva_monto es lo que resta para
      // llegar al monto exacto. Así base + iva = monto SIEMPRE (cuadra al centavo).
      const r2 = (n: number) => Math.round(n * 100) / 100;
      const precioUnitario = ivaPct > 0 ? r2(montoTotal / (1 + ivaPct / 100)) : montoTotal;
      const ivaMonto = r2(montoTotal - precioUnitario);
      return posRpc<FacturaRecargaResp>('pos_registrar_venta', {
        payload: {
          caja_id: caja.id,
          cliente_partner_id: cliente.id,
          tipo_doc: 'factura',
          observaciones: null,
          precio_fijo: true,
          // 901: la venta queda enlazada a la recarga EN LA MISMA transacción.
          // Sin esto no hay forma de saber qué recargas se entregaron y no se
          // facturaron — que es como se nos escapó la del 24/09.
          bn_tx_id: r.id,
          lineas: [
            {
              producto_id: prodInvId,
              cantidad: 1,
              precio_unitario: precioUnitario,
              descuento: 0,
              iva_tarifa: ivaPct,
              iva_monto: ivaMonto,   // 658: IVA exacto (residual) → total = monto, sin vuelto
              descripcion: `RECARGA ${r.proveedor} ${r.celular}`,
            },
            // Segunda línea SÓLO si hay comisión: con la comisión desactivada
            // la factura sale exactamente igual que antes.
            ...(gestion > 0 ? [{
              producto_id: gestionProdId!,
              cantidad: 1,
              precio_unitario: gestionBase,
              descuento: 0,
              iva_tarifa: gestionIvaPct,
              iva_monto: gestionIva,
              descripcion: 'COMISION DE GESTION',
            }] : []),
          ],
          pagos: [{ forma_pago_id: efectivoFormaId, monto: cobrado, referencia: null }],
        },
      });
    },
    onSuccess: async (fr, r) => {
      const recibido = Number(r.monto_cobrado) || Number(r.monto) || fr.total;  // lo realmente cobrado en efectivo
      const tk: TicketImprimible = {
        codigo: fr.codigo, tipoDoc: 'factura', claveAcceso: fr.clave_acceso ?? null, serie: fr.serie ?? null,
        caja: caja.nombre, cajero, cliente: cliente?.razon_social ?? 'CONSUMIDOR FINAL',
        lineas: fr.lineas, subtotal: fr.subtotal, iva: fr.iva, total: fr.total,
        recibido, vuelto: fr.vuelto ?? Math.max(recibido - fr.total, 0), pagos: [{ forma: 'Efectivo', monto: recibido }],
      };
      setFacturaOk({ codigo: fr.codigo, ticket: tk });
      setFacturaError(null);
      await imprimirTicket(tk, emp);
      toast.success(`Factura ${fr.codigo} emitida por la recarga`);
    },
    onError: (e) => {
      const msg = getErrorMessage(e);
      setFacturaError(msg);
      toast.error(`La recarga quedó acreditada, pero la factura NO se emitió: ${msg}. Reintenta con el botón.`);
    },
  });

  const vender = useMutation({
    mutationFn: () => bnVender(caja.id, productoId, celular.trim(), Number(monto) || 0),
    onMutate: () => setVentaError(null),
    onSuccess: (r) => {
      setResultado(r); setFacturaOk(null); setFacturaError(null); setVentaError(null); invalidar();
      if (r.estado === 'aprobada') {
        toast.success(`Recarga aprobada · aut. ${r.autorizacion}`);
        if (facturaComercio) facturar.mutate(r);
        else {
          imprimirVoucher(r);
        }
      }
      else if (r.estado === 'declinada') toast.error(`Declinada: ${r.mensaje ?? ''}`);
      else toast.warning('Recarga pendiente de regularizar');
    },
    onError: (e) => { const msg = getErrorMessage(e); setVentaError(msg); toast.error(msg); },
  });
  const regularizar = useMutation({
    mutationFn: (id: string) => bnRegularizar(id),
    onSuccess: (r: { estado: string; ya_resuelta?: boolean }) => {
      if (r.ya_resuelta) toast.info(`Ya se había resuelto sola: ${r.estado}`);
      else toast.success(`Regularizada: ${r.estado}`);
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // En PRODUCCIÓN, bn_vender devuelve 'pendiente' de inmediato: el worker resuelve
  // el estado/mensaje real del WS de Broadnet de forma asíncrona. Sondeamos bn_ws_log
  // hasta que el estado cambie, para mostrarle al cajero el mensaje final del WS.
  const esProduccion = bnCfg?.modo === 'produccion';
  const enSeguimiento = !!resultado && ['pendiente', 'reverso_pendiente', 'confirmacion_pendiente'].includes(resultado.estado);
  const sondeoActivo = esProduccion && enSeguimiento;
  const { data: seguimiento } = useQuery<BnWsLog>({
    queryKey: ['pos-bn-tx-seguimiento', resultado?.id],
    queryFn: () => bnWsLog(resultado!.id),
    enabled: sondeoActivo,
    refetchInterval: sondeoActivo ? 2500 : false,
    staleTime: 0,
  });
  useEffect(() => {
    if (!seguimiento || !resultado) return;
    const tx = seguimiento.tx;
    if (tx.estado === resultado.estado) return;
    const final = {
      ...resultado,
      estado: tx.estado,
      mensaje: tx.mensaje ?? resultado.mensaje,
      codigo_respuesta: tx.codigo_respuesta ?? resultado.codigo_respuesta,
      autorizacion: tx.autorizacion ?? resultado.autorizacion,
    };
    setResultado(final);
    invalidar();
    // Desenlace asíncrono del worker (producción): disparar la MISMA salida que
    // la aprobación inmediata — en modo comercio, FACTURA con la secuencia
    // normal del POS según la caja activa; si no, voucher. (Antes esto solo
    // pasaba en simulado y en producción la recarga aprobada quedaba SIN factura.)
    if (tx.estado === 'aprobada' || tx.estado === 'confirmada') {
      toast.success(`Recarga ${tx.estado} · aut. ${tx.autorizacion ?? '—'}`);
      if (facturaComercio) facturar.mutate(final);
      else imprimirVoucher(final);
    } else if (tx.estado === 'declinada' || tx.estado === 'fallida') {
      toast.error(`Recarga ${tx.estado.replace('_', ' ')}: ${tx.mensaje ?? ''}`);
    } else if (tx.estado === 'reversada') {
      // Producto con anulación: el reverso aplicó — la recarga NO se realizó y
      // NO se emitió factura (la factura solo nace con aprobada/confirmada).
      toast.info('Recarga NO realizada — reversada ante Broadnet; no se cobró ni se facturó');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seguimiento, resultado, invalidar, facturaComercio]);

  const celOk = celular.trim().length >= 7 && celular.trim() === celular2.trim();
  // 914: con tarjeta, la recarga desglosada DEBE ser exactamente el precio de la tarjeta.
  const tarjetaCuadra = montoTarjeta == null || (!calculando && !!desglose && Number(desglose.recarga) === montoTarjeta);
  const listo = !!productoId && (Number(monto) || 0) > 0 && celOk && pendientes.length === 0 && tarjetaCuadra;
  const bloqueada = pendientes.length > 0;

  return (
    <ModalBox title="Recargas Broadnet (F4)" onClose={onClose} wide>
      <div className="flex items-center gap-2 border-b mb-3">
        {([['recargas', 'Recargas móviles/TV'], ['servicios', 'Pago de servicios (agua/luz/otros)']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTabBn(k)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tabBn === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>
      {tabBn === 'servicios' ? <ServiciosBasicos visual /> : (
      <div className="space-y-3">
        {bloqueada && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900/40 dark:bg-red-900/15">
            <p className="font-medium text-red-700 dark:text-red-300 mb-2">
              Hay {pendientes.length} recarga(s) pendiente(s) de regularizar — no se puede vender otra (norma Broadnet).
            </p>
            {pendientes.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-0.5">
                <span className="text-xs capitalize">{t.estado.replace('_', ' ')}</span>
                <Button size="sm" variant="outline" isLoading={regularizar.isPending} onClick={() => regularizar.mutate(t.id)}>Regularizar</Button>
              </div>
            ))}
          </div>
        )}

        {ventaError && !resultado && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900/40 dark:bg-red-900/15">
            <p className="font-medium text-red-700 dark:text-red-300">✗ {ventaError}</p>
          </div>
        )}

        {!resultado && (
          <>
            {/* 911: catálogo visual — marca → tarjeta con el monto → solo los datos.
                Sale el MISMO producto_id + monto que antes daba el desplegable. */}
            {!producto ? (
              <SelectorCatalogo productos={productos} moneda={moneda}
                onElegir={(p, m) => { void elegirTarjeta(p, m); }} />
            ) : (
              <>
                <div className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: marcaDe(producto).color }}>
                  <MarcaLogo marca={marcaDe(producto)} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{marcaDe(producto).nombre} · {producto.servicio_nombre.replace(/_/g, ' ')}</div>
                    {!montoLibre && montoTarjeta != null && (
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">Recarga</span>
                        <span className="text-xl font-extrabold tabular-nums" style={{ color: marcaDe(producto).color }}>{money(montoTarjeta, moneda)}</span>
                      </div>
                    )}
                  </div>
                  {/* Lo que se COBRA: grande y en ámbar suave — destaca sin gritar. */}
                  {(calculando || montoNum > 0) && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-right dark:border-amber-400/30 dark:bg-amber-400/10">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-600/80 dark:text-amber-300/80">Cobrar</div>
                      {calculando
                        ? <Loader2 className="ml-auto h-7 w-7 animate-spin text-amber-500" />
                        : <div className="text-3xl font-extrabold leading-tight tabular-nums text-amber-700 dark:text-amber-300">
                            {money(desglose ? Number(desglose.total) : montoNum, moneda)}
                          </div>}
                    </div>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setProductoId(''); setMonto(''); setMontoLibre(false); setMontoTarjeta(null); }}>Cambiar</Button>
                </div>
                {montoLibre && (
                  <label className="block space-y-1"><span className="text-xs text-muted-foreground">Monto $ *</span>
                    <input type="number" min="0" step="0.25" autoFocus className={`${fieldCls} h-12 text-right text-2xl font-bold`} value={monto} onChange={(e) => setMonto(e.target.value)} /></label>
                )}
                <label className="block space-y-1"><span className="text-xs text-muted-foreground">{producto.campos_requeridos?.[0]?.label || 'Celular / cuenta'} *</span>
                  <input inputMode="numeric" autoFocus={!montoLibre} className={`${fieldCls} h-12 text-xl tracking-widest`} value={celular} onChange={(e) => setCelular(e.target.value)} placeholder="09XXXXXXXX" /></label>
                {desglose && Number(desglose.comision) > 0 && (
                  <p className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                    Se recargan <b className="tabular-nums">{money(desglose.recarga, moneda)}</b> y se cobran{' '}
                    <b className="tabular-nums">{money(desglose.total, moneda)}</b>
                    <span className="text-muted-foreground"> · comisión de gestión {money(desglose.comision, moneda)}</span>
                  </p>
                )}
                <label className="block space-y-1"><span className="text-xs text-muted-foreground">Confirma el número * (dígito a dígito)</span>
                  <input inputMode="numeric" className={`${fieldCls} h-12 text-xl tracking-widest ${celular2 && celular.trim() !== celular2.trim() ? 'border-red-400' : ''}`} value={celular2}
                    onChange={(e) => setCelular2(e.target.value)} onPaste={(e) => e.preventDefault()}
                    onKeyDown={(e) => { if (e.key === 'Enter' && listo && !vender.isPending) vender.mutate(); }} /></label>
                {celular2 !== '' && celular.trim() !== celular2.trim() && <p className="text-xs text-red-600">Los números no coinciden</p>}
              </>
            )}
            {producto && <p className="text-xs text-muted-foreground">Cobro <span className="font-medium">solo en EFECTIVO</span>: recibe {money(desglose && Number(desglose.comision) > 0 ? Number(desglose.total) : (Number(monto) || 0), moneda)} antes de confirmar. La nota de venta la emite Broadnet.</p>}
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button isLoading={vender.isPending} disabled={!listo} onClick={() => vender.mutate()}>Recargar y cobrar</Button>
            </div>
          </>
        )}

        {resultado && (
          <div className="space-y-3 text-center">
            {resultado.estado === 'aprobada' && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-900/15">
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">✓ RECARGA APROBADA</p>
                <p className="mt-1 text-sm">{resultado.proveedor} · {resultado.celular} · <span className="font-semibold">{money(resultado.monto, moneda)}</span></p>
                <p className="mt-2 font-mono text-sm">Autorización: <span className="font-bold">{resultado.autorizacion}</span></p>
                {resultado.nota_venta && <p className="font-mono text-xs text-muted-foreground">Nota de venta Broadnet: {resultado.nota_venta.trim()}</p>}
                <p className="font-mono text-xs text-muted-foreground">Ref: {resultado.referencia}</p>
                {facturaComercio ? (
                  <>
                    {facturaOk && (
                      <>
                        <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">Factura {facturaOk.codigo} emitida</p>
                        <Button size="sm" variant="outline" className="mt-2" onClick={() => void imprimirTicket(facturaOk.ticket, emp)}>Reimprimir factura</Button>
                      </>
                    )}
                    {facturaError && (
                      <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-left dark:border-red-900/40 dark:bg-red-900/15">
                        <p className="text-xs text-red-700 dark:text-red-300">La recarga quedó acreditada, pero la factura falló: {facturaError}</p>
                        <Button size="sm" variant="outline" className="mt-1" isLoading={facturar.isPending} onClick={() => facturar.mutate(resultado)}>Reintentar factura</Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => imprimirVoucher(resultado)}>Reimprimir voucher</Button>
                )}
              </div>
            )}
            {resultado.estado === 'declinada' && (
              <div className="rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/15">
                <p className="text-lg font-bold text-red-700 dark:text-red-300">✗ DECLINADA</p>
                <p className="text-sm">{resultado.mensaje ?? ''} (código {resultado.codigo_respuesta})</p>
                <p className="text-xs text-muted-foreground mt-1">NO cobres al cliente.</p>
              </div>
            )}
            {resultado.estado === 'fallida' && (
              <div className="rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/15">
                <p className="text-lg font-bold text-red-700 dark:text-red-300">✗ RECARGA FALLIDA</p>
                <p className="text-sm">{resultado.mensaje ?? ''} (código {resultado.codigo_respuesta})</p>
                <p className="text-xs text-muted-foreground mt-1">NO cobres al cliente.</p>
              </div>
            )}
            {resultado.estado === 'reversada' && (
              <div className="rounded-md border border-slate-300 bg-slate-50 p-4 dark:border-slate-700/40 dark:bg-slate-800/20">
                <p className="text-lg font-bold text-slate-700 dark:text-slate-300">↩ REVERSADA — la recarga no se aplicó</p>
                {resultado.mensaje && <p className="text-sm">{resultado.mensaje}</p>}
                <p className="text-xs text-muted-foreground mt-1">El cliente no fue cobrado / devuélvele el efectivo.</p>
              </div>
            )}
            {(resultado.estado === 'reverso_pendiente' || resultado.estado === 'confirmacion_pendiente' || resultado.estado === 'pendiente') && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/15">
                <p className="text-lg font-bold text-amber-700 dark:text-amber-300">⚠ SIN RESPUESTA</p>
                <p className="text-sm">La transacción quedó <span className="font-medium capitalize">{resultado.estado.replace('_', ' ')}</span>. Regularízala antes de vender otra recarga.</p>
                {(resultado.mensaje || resultado.codigo_respuesta) && (
                  <p className="text-sm mt-1">{resultado.mensaje ?? ''} {resultado.codigo_respuesta ? `(código ${resultado.codigo_respuesta})` : ''}</p>
                )}
                {sondeoActivo && (
                  <p className="mt-2 flex items-center justify-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Esperando respuesta del WebService…
                  </p>
                )}
                <Button size="sm" variant="outline" className="mt-2" isLoading={regularizar.isPending} onClick={() => regularizar.mutate(resultado.id)}>Regularizar ahora</Button>
              </div>
            )}
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              <Button onClick={() => { setResultado(null); setFacturaOk(null); setFacturaError(null); setVentaError(null); setProductoId(''); setMonto(''); setMontoLibre(false); setMontoTarjeta(null); setCelular(''); setCelular2(''); }}>Nueva recarga</Button>
            </div>
          </div>
        )}
      </div>
      )}
    </ModalBox>
  );
}

// Cola de ventas offline: qué está pendiente de sincronizar, errores y acciones.
function ColaOffline({ online, onSync, onChange, onClose }: { online: boolean; onSync: () => void; onChange: () => void; onClose: () => void }) {
  const dlg = useDialogoEnLinea();
  const [cola, setCola] = useState<VentaOffline[]>([]);
  const cargar = useCallback(() => { void ventasPendientes().then(setCola); }, []);
  useEffect(() => { cargar(); }, [cargar]);
  return (
    <ModalBox title={online ? 'Ventas offline por sincronizar' : 'MODO OFFLINE — ventas en cola'} onClose={onClose} wide>
      {dlg.ui}
      {!online && (
        <p className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          Sin conexión: puedes seguir vendiendo (solo EFECTIVO). Las facturas salen con su clave de acceso
          de la reserva; todo se sincroniza automáticamente al volver la red.
        </p>
      )}
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-2 pr-3">Hora</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Serie</th>
          <th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3"></th>
        </tr></thead>
        <tbody>
          {cola.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Nada pendiente — todo sincronizado ✓</td></tr>}
          {cola.map((v) => (
            <tr key={v.client_uuid} className="border-b last:border-0">
              <td className="py-1.5 pr-3 font-mono text-xs">{new Date(v.fecha).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</td>
              <td className="py-1.5 pr-3 text-xs">{v.tipo_doc === 'nota_venta' ? 'Nota venta' : 'Factura'}</td>
              <td className="py-1.5 pr-3 font-mono text-xs">{v.serie ?? '—'}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(v.total)}</td>
              <td className="py-1.5 pr-3 text-xs">
                {v.error
                  ? <span className="text-red-600 dark:text-red-400" title={v.error}>Error: {v.error.slice(0, 60)}</span>
                  : <span className="text-amber-600 dark:text-amber-400">en cola</span>}
              </td>
              <td className="py-1.5 pr-2 text-right">
                {v.error && (
                  <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Descartar esta venta de la cola (NO se registrará — asegúrate de re-digitarla)"
                    onClick={async () => { if (await dlg.confirmar('¿Descartar esta venta offline?', { detalle: 'NO quedará registrada en el sistema.', peligro: true })) { void descartarVentaOffline(v.client_uuid).then(() => { cargar(); onChange(); }); } }}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {online && cola.length > 0 && (
        <div className="mt-3 flex justify-end">
          <Button onClick={() => { onSync(); setTimeout(cargar, 1500); }}><RefreshCw className="w-4 h-4 mr-1" /> Sincronizar ahora</Button>
        </div>
      )}
    </ModalBox>
  );
}
